<template>
  <Transition name="modal">
    <div
      v-if="isOpen"
      class="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-2"
      @click.self="close"
    >
      <div class="bg-slate-900 rounded-2xl shadow-2xl w-full h-full max-w-[98vw] max-h-[95vh] overflow-hidden flex flex-col">
        <!-- Header -->
        <div class="bg-gradient-to-r from-blue-600 to-cyan-600 text-white p-4 flex-shrink-0">
          <div class="flex items-center justify-between">
            <div class="flex items-center gap-3">
              <div class="w-10 h-10 rounded-xl bg-white/20 flex items-center justify-center">
                <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
              </div>
              <div>
                <h2 class="text-xl font-bold">SEC Mono Log Analyzer</h2>
                <p class="text-blue-100 text-sm">Diagnostika Copeland tepelnych cerpadel</p>
              </div>
            </div>
            <div class="flex items-center gap-2">
              <!-- Tabs -->
              <div class="hidden md:flex items-center gap-1 bg-white/10 rounded-lg p-1">
                <button
                  v-for="tab in tabs"
                  :key="tab.id"
                  @click="activeTab = tab.id"
                  :class="[
                    'px-3 py-1.5 rounded-md text-sm font-medium transition-all',
                    activeTab === tab.id
                      ? 'bg-white text-blue-600'
                      : 'text-white/80 hover:bg-white/10'
                  ]"
                >
                  {{ tab.label }}
                </button>
              </div>
              <button
                @click="close"
                class="text-white hover:bg-white hover:bg-opacity-20 rounded-full p-2 transition-colors"
              >
                <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>
        </div>

        <!-- Mobile Tabs -->
        <div class="md:hidden flex border-b border-slate-700 bg-slate-800">
          <button
            v-for="tab in tabs"
            :key="tab.id"
            @click="activeTab = tab.id"
            :class="[
              'flex-1 px-3 py-2 text-sm font-medium transition-all border-b-2',
              activeTab === tab.id
                ? 'text-blue-400 border-blue-400'
                : 'text-slate-400 border-transparent hover:text-slate-200'
            ]"
          >
            {{ tab.label }}
          </button>
        </div>

        <!-- Content -->
        <div class="flex-1 overflow-hidden flex">
          <!-- Left Sidebar - Log Files & Parameters -->
          <div class="w-80 border-r border-slate-700 flex flex-col overflow-hidden bg-slate-800/50">
            <!-- Log Files Section -->
            <div class="p-4 border-b border-slate-700">
              <div class="flex items-center justify-between mb-3">
                <h3 class="text-sm font-semibold text-slate-200">Log soubory</h3>
                <label class="flex items-center gap-1 px-2 py-1 bg-blue-600 hover:bg-blue-500 rounded cursor-pointer transition-colors text-xs">
                  <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                  </svg>
                  <span>Pridat</span>
                  <input type="file" accept=".txt,.csv,.log" @change="handleFileUpload" class="hidden" />
                </label>
              </div>

              <!-- Empty state dropzone -->
              <div
                v-show="logs.length === 0"
                :class="[
                  'text-center py-6 border-2 border-dashed rounded-lg transition-all cursor-pointer',
                  isDragging ? 'border-blue-500 bg-blue-500/10' : 'border-slate-600 hover:border-slate-500'
                ]"
                @dragover="handleDragOver"
                @dragleave="handleDragLeave"
                @drop="handleDrop"
                @click="$refs.sidebarFileInput.click()"
              >
                <svg :class="['w-8 h-8 mx-auto mb-2 transition-colors', isDragging ? 'text-blue-400' : 'text-slate-500']" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                </svg>
                <p :class="['text-xs transition-colors', isDragging ? 'text-blue-300' : 'text-slate-500']">
                  {{ isDragging ? 'Pustte soubor...' : 'Pretahnete log soubory' }}
                </p>
                <input
                  ref="sidebarFileInput"
                  type="file"
                  accept=".txt,.csv,.log"
                  @change="handleFileUpload"
                  class="hidden"
                />
              </div>

              <!-- File list when logs exist -->
              <div v-show="logs.length > 0" class="space-y-2 max-h-32 overflow-y-auto">
                <div
                  v-for="(log, index) in logs"
                  :key="index"
                  :class="[
                    'flex items-center gap-2 p-2 rounded-lg border cursor-pointer transition-all',
                    activeLogIndex === index
                      ? 'bg-slate-700/50 border-blue-500/50'
                      : 'bg-slate-800/30 border-slate-700/30 hover:border-slate-600'
                  ]"
                  @click="activeLogIndex = index"
                >
                  <div class="w-3 h-3 rounded-full flex-shrink-0" :style="{ backgroundColor: LOG_COLORS[index % LOG_COLORS.length] }"></div>
                  <div class="flex-1 min-w-0">
                    <p class="text-xs font-medium text-slate-200 truncate">{{ log.fileName }}</p>
                    <p class="text-xs text-slate-500">{{ log.data.length }} vzorku</p>
                  </div>
                  <button @click.stop="removeLog(index)" class="p-1 hover:bg-red-500/20 rounded text-slate-500 hover:text-red-400">
                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              </div>
            </div>

            <!-- Parameter Selector -->
            <div class="flex-1 overflow-y-auto p-4">
              <div class="flex items-center justify-between mb-3">
                <h3 class="text-sm font-semibold text-slate-200">Parametry</h3>
                <button @click="setDefaultParams" class="text-xs px-2 py-1 bg-blue-600 hover:bg-blue-500 rounded text-white">
                  Default
                </button>
              </div>

              <!-- Search -->
              <div class="relative mb-3">
                <svg class="absolute left-2 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
                <input
                  v-model="paramSearch"
                  type="text"
                  placeholder="Hledat..."
                  class="w-full pl-8 pr-3 py-1.5 bg-slate-800 border border-slate-600 rounded text-xs text-slate-200 placeholder-slate-500 focus:outline-none focus:border-blue-500"
                />
              </div>

              <!-- Selected params badges -->
              <div v-if="selectedParams.length > 0" class="flex flex-wrap gap-1 mb-3 pb-3 border-b border-slate-700">
                <span
                  v-for="param in selectedParams"
                  :key="param"
                  class="inline-flex items-center gap-1 px-2 py-0.5 bg-blue-600/30 border border-blue-500/50 rounded text-xs text-blue-200"
                >
                  {{ getParamLabel(param) }}
                  <button @click="toggleParam(param)" class="hover:text-white">
                    <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </span>
              </div>

              <!-- Parameter groups -->
              <div class="space-y-2">
                <div v-for="(group, groupKey) in filteredGroups" :key="groupKey" class="border border-slate-700/30 rounded-lg overflow-hidden">
                  <button
                    @click="toggleGroup(groupKey)"
                    class="w-full flex items-center justify-between p-2 bg-slate-800/30 hover:bg-slate-700/30 transition-colors"
                  >
                    <div class="flex items-center gap-2">
                      <div class="w-2.5 h-2.5 rounded" :style="{ backgroundColor: group.color }"></div>
                      <span class="text-xs font-medium text-slate-200">{{ group.name }}</span>
                      <span class="text-xs text-slate-500">({{ countSelectedInGroup(group) }}/{{ group.params.length }})</span>
                    </div>
                    <svg :class="['w-4 h-4 text-slate-400 transition-transform', expandedGroups.has(groupKey) ? 'rotate-180' : '']" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>

                  <div v-if="expandedGroups.has(groupKey)" class="p-2 grid grid-cols-1 gap-1">
                    <label
                      v-for="param in group.params"
                      :key="param.key"
                      class="flex items-center gap-2 p-1 rounded hover:bg-slate-700/30 cursor-pointer"
                    >
                      <input
                        type="checkbox"
                        :checked="selectedParams.includes(param.key)"
                        @change="toggleParam(param.key)"
                        class="w-3 h-3 rounded border-slate-600 bg-slate-800 text-blue-500"
                      />
                      <span class="text-xs text-slate-300 truncate">
                        {{ param.label }}
                        <span v-if="param.unit" class="text-slate-500">[{{ param.unit }}]</span>
                      </span>
                    </label>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Main Content Area -->
          <div class="flex-1 overflow-y-auto p-4 bg-slate-900">
            <!-- No logs state - Full dropzone -->
            <div
              v-if="logs.length === 0 && !isProcessing"
              class="h-full flex items-center justify-center"
              @dragover="handleDragOver"
              @dragleave="handleDragLeave"
              @drop="handleDrop"
            >
              <div
                :class="[
                  'text-center p-12 rounded-2xl border-2 border-dashed transition-all duration-200 cursor-pointer',
                  isDragging
                    ? 'border-blue-500 bg-blue-500/10 scale-105'
                    : 'border-slate-600 hover:border-slate-500 hover:bg-slate-800/30'
                ]"
                @click="$refs.mainFileInput.click()"
              >
                <svg :class="['w-20 h-20 mx-auto mb-4 transition-colors', isDragging ? 'text-blue-400' : 'text-slate-600']" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                </svg>
                <h3 :class="['text-xl font-semibold mb-2 transition-colors', isDragging ? 'text-blue-300' : 'text-slate-300']">
                  {{ isDragging ? 'Pustte soubor...' : 'Nahrajte log soubor' }}
                </h3>
                <p class="text-slate-500 text-sm max-w-md mb-4">
                  Pretahnete .txt nebo .csv log soubor ze SEC Mono sem nebo kliknete pro vyber
                </p>
                <div class="flex items-center justify-center gap-2 text-xs text-slate-600">
                  <span class="px-2 py-1 bg-slate-800 rounded">.txt</span>
                  <span class="px-2 py-1 bg-slate-800 rounded">.csv</span>
                  <span class="px-2 py-1 bg-slate-800 rounded">.log</span>
                </div>
                <input
                  ref="mainFileInput"
                  type="file"
                  accept=".txt,.csv,.log"
                  @change="handleFileUpload"
                  class="hidden"
                />
              </div>
            </div>

            <!-- Processing state -->
            <div
              v-else-if="isProcessing"
              class="h-full flex items-center justify-center"
            >
              <div class="text-center p-12 rounded-2xl border-2 border-blue-500/30 bg-blue-500/5 max-w-md w-full">
                <div class="w-16 h-16 mx-auto mb-4 relative">
                  <svg class="w-16 h-16 text-blue-500 animate-spin" fill="none" viewBox="0 0 24 24">
                    <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                    <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                </div>
                <h3 class="text-xl font-semibold mb-2 text-blue-300">Zpracovavam soubor...</h3>
                <p class="text-slate-400 text-sm mb-4 truncate">{{ processingProgress.fileName }}</p>

                <!-- Progress bar -->
                <div class="w-full bg-slate-700 rounded-full h-2 mb-2">
                  <div
                    class="bg-blue-500 h-2 rounded-full transition-all duration-100"
                    :style="{ width: progressPercent + '%' }"
                  ></div>
                </div>
                <p class="text-xs text-slate-500">
                  {{ processingProgress.current.toLocaleString() }} / {{ processingProgress.total.toLocaleString() }} radku
                  <span v-if="progressPercent > 0">({{ progressPercent.toFixed(0) }}%)</span>
                </p>
              </div>
            </div>

            <!-- Chart Tab -->
            <div v-else-if="activeTab === 'chart'" class="space-y-4">
              <div :class="['rounded-xl border p-4', lightTheme ? 'bg-white border-gray-200' : 'bg-slate-800/30 border-slate-700/50']">
                <div class="flex items-center justify-between mb-4">
                  <div class="flex items-center gap-4">
                    <h3 :class="['text-sm font-semibold', lightTheme ? 'text-gray-800' : 'text-slate-200']">
                      Casovy prubeh
                      <span v-if="selectedParams.length > 0" :class="['font-normal', lightTheme ? 'text-gray-500' : 'text-slate-500']">
                        ({{ selectedParams.length }} parametru)
                      </span>
                    </h3>
                    <!-- Time range selector -->
                    <div class="flex items-center rounded-lg overflow-hidden border" :class="lightTheme ? 'border-gray-200' : 'border-slate-600'">
                      <button
                        v-for="range in TIME_RANGES"
                        :key="range.key"
                        @click="timeRange = range.key; currentZoomRange = null; renderChart()"
                        :class="[
                          'px-2.5 py-1 text-xs font-medium transition-colors',
                          timeRange === range.key
                            ? (lightTheme ? 'bg-blue-500 text-white' : 'bg-blue-600 text-white')
                            : (lightTheme ? 'bg-gray-50 text-gray-600 hover:bg-gray-100' : 'bg-slate-700/50 text-slate-400 hover:bg-slate-600')
                        ]"
                      >
                        {{ range.label }}
                      </button>
                    </div>
                  </div>
                  <div class="flex items-center gap-2">
                    <!-- Defrost zones toggle -->
                    <button
                      v-if="defrosts.length > 0"
                      @click="toggleDefrostMode()"
                      :class="['flex items-center gap-1.5 px-2 py-1 rounded text-xs font-medium transition-colors', showDefrostZones ? 'bg-cyan-600 text-white' : (lightTheme ? 'bg-gray-100 text-gray-700 hover:bg-gray-200' : 'bg-slate-700 text-slate-300 hover:bg-slate-600')]"
                      title="Zobrazit defrost zony"
                    >
                      <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                      </svg>
                      Defrosty ({{ defrosts.length }})
                    </button>
                    <!-- Theme toggle -->
                    <button
                      @click="lightTheme = !lightTheme; renderChart()"
                      :class="['flex items-center gap-1.5 px-2 py-1 rounded text-xs font-medium transition-colors', lightTheme ? 'bg-gray-100 text-gray-700 hover:bg-gray-200' : 'bg-slate-700 text-slate-300 hover:bg-slate-600']"
                      :title="lightTheme ? 'Prepnout na tmavy' : 'Prepnout na svetly (pro tisk)'"
                    >
                      <svg v-if="lightTheme" class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
                      </svg>
                      <svg v-else class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
                      </svg>
                      {{ lightTheme ? 'Tmavy' : 'Svetly' }}
                    </button>
                    <!-- Refresh button -->
                    <button
                      @click="renderChart(); chartNeedsUpdate = false"
                      :class="['flex items-center gap-1.5 px-3 py-1 rounded text-xs font-medium transition-colors', chartNeedsUpdate ? 'bg-blue-600 text-white hover:bg-blue-500' : (lightTheme ? 'bg-gray-100 text-gray-600 hover:bg-gray-200' : 'bg-slate-700 text-slate-400 hover:bg-slate-600')]"
                      title="Obnovit graf"
                    >
                      <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                      </svg>
                      Obnovit
                    </button>
                  </div>
                </div>

                <div v-if="selectedParams.length === 0" :class="['h-64 flex items-center justify-center', lightTheme ? 'text-gray-500' : 'text-slate-500']">
                  Vyberte parametry v levem panelu
                </div>

                <div v-else class="h-[500px]">
                  <!-- Plotly Chart -->
                  <div ref="chartDiv" class="h-full w-full"></div>
                </div>

                <!-- Defrost Detail Panel -->
                <div v-if="selectedDefrost" class="mt-4 pt-4 border-t border-slate-700">
                  <div class="flex items-center justify-between mb-3">
                    <h4 class="text-sm font-semibold text-cyan-400 flex items-center gap-2">
                      <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                      </svg>
                      Defrost #{{ selectedDefrost.id }} - {{ formatTime(selectedDefrost.startTime) }}
                    </h4>
                    <button
                      @click="selectedDefrost = null; renderChart()"
                      class="p-1 hover:bg-slate-700 rounded text-slate-400 hover:text-slate-200"
                    >
                      <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>

                  <div class="grid grid-cols-2 lg:grid-cols-4 gap-4">
                    <!-- Zakladni info -->
                    <div class="bg-slate-800/50 rounded-lg p-3 border border-slate-700/30">
                      <div class="text-xs text-slate-500 mb-2 font-medium">Zakladni info</div>
                      <div class="space-y-1 text-sm">
                        <div class="flex justify-between">
                          <span class="text-slate-400">Delka</span>
                          <span class="text-slate-200 font-mono">{{ selectedDefrost.durationFormatted }}</span>
                        </div>
                        <div class="flex justify-between">
                          <span class="text-slate-400">Duvod</span>
                          <span class="text-slate-200">{{ getDefrostReason(selectedDefrost.reason) }}</span>
                        </div>
                        <div class="flex justify-between">
                          <span class="text-slate-400">Zacatek</span>
                          <span class="text-slate-200 font-mono text-xs">{{ formatTime(selectedDefrost.startTime) }}</span>
                        </div>
                        <div class="flex justify-between">
                          <span class="text-slate-400">Konec</span>
                          <span class="text-slate-200 font-mono text-xs">{{ formatTime(selectedDefrost.endTime) }}</span>
                        </div>
                        <div class="flex justify-between">
                          <span class="text-slate-400">Vzorky</span>
                          <span class="text-slate-200 font-mono text-xs">{{ selectedDefrost.sampleCount }} @ {{ selectedDefrost.sampleInterval }}s</span>
                        </div>
                      </div>
                    </div>

                    <!-- Teploty -->
                    <div class="bg-slate-800/50 rounded-lg p-3 border border-slate-700/30">
                      <div class="text-xs text-slate-500 mb-2 font-medium">Teploty (zacatek / konec)</div>
                      <div class="space-y-1 text-sm">
                        <div class="flex justify-between">
                          <span class="text-slate-400">Ambient (T2)</span>
                          <span class="text-orange-400 font-mono">{{ selectedDefrost.ambient?.start?.toFixed(1) }} / {{ selectedDefrost.ambient?.end?.toFixed(1) }}°C</span>
                        </div>
                        <div class="flex justify-between">
                          <span class="text-slate-400">Vyparnik (T3)</span>
                          <span class="text-blue-400 font-mono">{{ selectedDefrost.evapSuction?.start?.toFixed(1) }} / {{ selectedDefrost.evapSuction?.end?.toFixed(1) }}°C</span>
                        </div>
                        <div class="flex justify-between">
                          <span class="text-slate-400">Te (vypar.)</span>
                          <span class="text-cyan-400 font-mono">{{ selectedDefrost.te?.start?.toFixed(1) }} / {{ selectedDefrost.te?.end?.toFixed(1) }}°C</span>
                        </div>
                        <div class="flex justify-between">
                          <span class="text-slate-400">Tc (kond.)</span>
                          <span class="text-green-400 font-mono">{{ selectedDefrost.tc?.start?.toFixed(1) }} / {{ selectedDefrost.tc?.end?.toFixed(1) }}°C</span>
                        </div>
                      </div>
                    </div>

                    <!-- Te statistiky -->
                    <div class="bg-slate-800/50 rounded-lg p-3 border border-slate-700/30">
                      <div class="text-xs text-slate-500 mb-2 font-medium">Te statistiky</div>
                      <div class="space-y-1 text-sm">
                        <div class="flex justify-between">
                          <span class="text-slate-400">Te min/max</span>
                          <span class="text-cyan-400 font-mono">{{ selectedDefrost.te?.min?.toFixed(1) }} / {{ selectedDefrost.te?.max?.toFixed(1) }}°C</span>
                        </div>
                        <div class="flex justify-between">
                          <span class="text-slate-400">Pod -10°C</span>
                          <span :class="['font-mono', selectedDefrost.timeBelow?.minus10 > 0 ? 'text-yellow-400' : 'text-slate-500']">{{ formatSeconds(selectedDefrost.timeBelow?.minus10) }}</span>
                        </div>
                        <div class="flex justify-between">
                          <span class="text-slate-400">Pod -15°C</span>
                          <span :class="['font-mono', selectedDefrost.timeBelow?.minus15 > 0 ? 'text-orange-400' : 'text-slate-500']">{{ formatSeconds(selectedDefrost.timeBelow?.minus15) }}</span>
                        </div>
                        <div class="flex justify-between">
                          <span class="text-slate-400">Pod -20°C</span>
                          <span :class="['font-mono', selectedDefrost.timeBelow?.minus20 > 0 ? 'text-red-400' : 'text-slate-500']">{{ formatSeconds(selectedDefrost.timeBelow?.minus20) }}</span>
                        </div>
                        <div class="flex justify-between">
                          <span class="text-slate-400">Pod -25°C</span>
                          <span :class="['font-mono', selectedDefrost.timeBelow?.minus25 > 0 ? 'text-red-500' : 'text-slate-500']">{{ formatSeconds(selectedDefrost.timeBelow?.minus25) }}</span>
                        </div>
                      </div>
                    </div>

                    <!-- Provozni hodnoty -->
                    <div class="bg-slate-800/50 rounded-lg p-3 border border-slate-700/30">
                      <div class="text-xs text-slate-500 mb-2 font-medium">Provozni hodnoty</div>
                      <div class="space-y-1 text-sm">
                        <div class="flex justify-between">
                          <span class="text-slate-400">Kompresor</span>
                          <span class="text-purple-400 font-mono">{{ selectedDefrost.compressorSpeed?.min }} - {{ selectedDefrost.compressorSpeed?.max }} Hz</span>
                        </div>
                        <div class="flex justify-between">
                          <span class="text-slate-400">EXV</span>
                          <span class="text-pink-400 font-mono">{{ selectedDefrost.exv?.min?.toFixed(0) }} - {{ selectedDefrost.exv?.max?.toFixed(0) }}%</span>
                        </div>
                        <div class="flex justify-between">
                          <span class="text-slate-400">Delta T start</span>
                          <span class="text-orange-400 font-mono">{{ selectedDefrost.deltaTeAmbient?.start?.toFixed(1) }}K</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Analysis Tab -->
            <div v-else-if="activeTab === 'analysis'" class="space-y-4">
              <div v-if="stats" class="space-y-4">
                <!-- Overview Cards -->
                <div class="grid grid-cols-4 gap-3">
                  <div class="bg-slate-800/30 rounded-lg p-3 border border-slate-700/30">
                    <div class="text-xs text-slate-500 mb-1">Celkem vzorku</div>
                    <div class="text-lg font-bold text-slate-200">{{ stats.totalSamples }}</div>
                  </div>
                  <div class="bg-slate-800/30 rounded-lg p-3 border border-slate-700/30">
                    <div class="text-xs text-slate-500 mb-1">Kompresor bezi</div>
                    <div class="text-lg font-bold text-green-400">{{ stats.runningPercentage.toFixed(1) }}%</div>
                  </div>
                  <div class="bg-slate-800/30 rounded-lg p-3 border border-slate-700/30">
                    <div class="text-xs text-slate-500 mb-1">Avg COP</div>
                    <div class="text-lg font-bold text-blue-400">{{ stats.cop?.avg?.toFixed(2) || '-' }}</div>
                  </div>
                  <div class="bg-slate-800/30 rounded-lg p-3 border border-slate-700/30">
                    <div class="text-xs text-slate-500 mb-1">Delka zaznamu</div>
                    <div class="text-lg font-bold text-slate-200">{{ formatDuration(stats.duration) }}</div>
                  </div>
                </div>

                <!-- Mode Distribution -->
                <div class="grid grid-cols-3 gap-3">
                  <div class="bg-red-500/10 border border-red-500/30 rounded-lg p-3">
                    <div class="text-xs text-red-300 mb-1">Topeni</div>
                    <div class="text-xl font-bold text-red-400">{{ stats.heatingPercentage.toFixed(1) }}%</div>
                  </div>
                  <div class="bg-blue-500/10 border border-blue-500/30 rounded-lg p-3">
                    <div class="text-xs text-blue-300 mb-1">Chlazeni</div>
                    <div class="text-xl font-bold text-blue-400">{{ stats.coolingPercentage.toFixed(1) }}%</div>
                  </div>
                  <div class="bg-cyan-500/10 border border-cyan-500/30 rounded-lg p-3">
                    <div class="text-xs text-cyan-300 mb-1">Defrost</div>
                    <div class="text-xl font-bold text-cyan-400">{{ stats.defrostPercentage.toFixed(1) }}%</div>
                  </div>
                </div>

                <!-- Detailed Stats -->
                <div class="grid grid-cols-3 gap-4">
                  <div class="bg-slate-800/30 rounded-xl border border-slate-700/50 p-4">
                    <h4 class="text-sm font-medium text-slate-400 border-b border-slate-700 pb-2 mb-3">Kompresor</h4>
                    <div class="space-y-2 text-sm">
                      <div class="flex justify-between">
                        <span class="text-slate-500">Otacky</span>
                        <span class="text-slate-200">{{ stats.speed?.min?.toFixed(0) }} - {{ stats.speed?.max?.toFixed(0) }} RPM</span>
                      </div>
                      <div class="flex justify-between">
                        <span class="text-slate-500">Prehrati</span>
                        <span class="text-slate-200">{{ stats.superheat?.avg?.toFixed(1) }} K</span>
                      </div>
                      <div class="flex justify-between">
                        <span class="text-slate-500">EXV</span>
                        <span class="text-slate-200">{{ stats.exv?.avg?.toFixed(1) }} %</span>
                      </div>
                    </div>
                  </div>

                  <div class="bg-slate-800/30 rounded-xl border border-slate-700/50 p-4">
                    <h4 class="text-sm font-medium text-slate-400 border-b border-slate-700 pb-2 mb-3">Teploty</h4>
                    <div class="space-y-2 text-sm">
                      <div class="flex justify-between">
                        <span class="text-slate-500">DLT</span>
                        <span :class="stats.dlt?.max > 130 ? 'text-red-400' : 'text-slate-200'">
                          {{ stats.dlt?.max?.toFixed(1) }} °C max
                        </span>
                      </div>
                      <div class="flex justify-between">
                        <span class="text-slate-500">Te</span>
                        <span class="text-slate-200">{{ stats.te?.min?.toFixed(1) }} - {{ stats.te?.max?.toFixed(1) }} °C</span>
                      </div>
                      <div class="flex justify-between">
                        <span class="text-slate-500">Tc</span>
                        <span class="text-slate-200">{{ stats.tc?.min?.toFixed(1) }} - {{ stats.tc?.max?.toFixed(1) }} °C</span>
                      </div>
                    </div>
                  </div>

                  <div class="bg-slate-800/30 rounded-xl border border-slate-700/50 p-4">
                    <h4 class="text-sm font-medium text-slate-400 border-b border-slate-700 pb-2 mb-3">Alarmy</h4>
                    <div v-if="alarms.software.length === 0 && alarms.hardware.length === 0" class="flex items-center gap-2 text-green-400 text-sm">
                      <div class="w-2 h-2 rounded-full bg-green-400"></div>
                      Zadne alarmy
                    </div>
                    <div v-else class="space-y-2">
                      <div v-if="alarms.software.length > 0">
                        <span class="text-xs text-slate-500">SW:</span>
                        <div class="flex flex-wrap gap-1 mt-1">
                          <span v-for="alarm in alarms.software" :key="alarm" class="px-1.5 py-0.5 bg-yellow-500/20 border border-yellow-500/30 rounded text-xs text-yellow-300">
                            {{ alarm }}
                          </span>
                        </div>
                      </div>
                      <div v-if="alarms.hardware.length > 0">
                        <span class="text-xs text-slate-500">HW:</span>
                        <div class="flex flex-wrap gap-1 mt-1">
                          <span v-for="alarm in alarms.hardware" :key="alarm" class="px-1.5 py-0.5 bg-red-500/20 border border-red-500/30 rounded text-xs text-red-300">
                            {{ alarm }}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Diagnostics Tab -->
            <div v-else-if="activeTab === 'diagnostics'" class="space-y-4">
              <div v-if="diagnostics" class="space-y-6">
                <!-- Overall Score -->
                <div :class="['rounded-xl border p-6', getScoreBg(diagnostics.scores.overall)]">
                  <div class="flex items-center justify-between">
                    <div>
                      <h3 class="text-lg font-semibold text-slate-200">Celkove hodnoceni systemu</h3>
                      <p class="text-sm text-slate-400 mt-1">{{ activeLog?.fileName }}</p>
                    </div>
                    <div class="text-right">
                      <div :class="['text-5xl font-bold', getScoreColor(diagnostics.scores.overall)]">
                        {{ diagnostics.scores.overall }}
                      </div>
                      <div class="text-sm text-slate-400">/ 100</div>
                    </div>
                  </div>

                  <!-- Sub-scores -->
                  <div class="grid grid-cols-4 gap-4 mt-6">
                    <div v-for="item in scoreItems" :key="item.label" class="bg-slate-800/50 rounded-lg p-3 border border-slate-700/30">
                      <div class="text-xs text-slate-500 mb-2">{{ item.label }}</div>
                      <div :class="['text-2xl font-bold', getScoreColor(item.score)]">{{ item.score }}</div>
                      <div class="mt-2 h-1.5 bg-slate-700 rounded-full overflow-hidden">
                        <div
                          :class="['h-full rounded-full transition-all', getScoreBarColor(item.score)]"
                          :style="{ width: item.score + '%' }"
                        ></div>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Issues List -->
                <div class="bg-slate-800/30 rounded-xl border border-slate-700/50 p-6">
                  <h3 class="text-lg font-semibold text-slate-200 mb-4">
                    Diagnostika a doporuceni
                    <span class="text-sm font-normal text-slate-500 ml-2">
                      ({{ issues.filter(i => i.severity === 'critical').length }} kritickych, {{ issues.filter(i => i.severity === 'warning').length }} varovani)
                    </span>
                  </h3>

                  <div v-if="issues.length === 0" class="text-center py-6 text-slate-500">
                    <svg class="w-8 h-8 mx-auto mb-2 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <p>Zadne problemy nebyly detekovany</p>
                  </div>

                  <div v-else class="space-y-3 max-h-96 overflow-y-auto pr-2">
                    <div
                      v-for="(issue, index) in issues"
                      :key="index"
                      :class="['rounded-lg border p-4', getSeverityStyles(issue.severity)]"
                    >
                      <div class="flex items-start gap-3">
                        <div class="text-xl">{{ getSeverityIcon(issue.severity) }}</div>
                        <div class="flex-1">
                          <div class="flex items-center gap-2 mb-1">
                            <span class="text-xs px-2 py-0.5 rounded bg-slate-700/50 text-slate-400">{{ issue.category }}</span>
                            <h4 class="font-medium">{{ issue.title }}</h4>
                          </div>
                          <p class="text-sm opacity-80 mb-2">{{ issue.description }}</p>
                          <div class="flex items-center gap-2 text-sm">
                            <span class="text-slate-500">Doporuceni:</span>
                            <span class="opacity-90">{{ issue.recommendation }}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Defrosts Tab -->
            <div v-else-if="activeTab === 'defrosts'" class="space-y-4">
              <div v-if="defrosts.length === 0" class="text-center py-12">
                <svg class="w-12 h-12 mx-auto mb-3 text-slate-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                <p class="text-slate-500">Zadne defrosty v logu</p>
              </div>

              <!-- Loading indicator for analysis -->
              <div v-else-if="defrostAnalysisProgress.processing" class="flex flex-col items-center justify-center py-16">
                <div class="relative w-24 h-24 mb-6">
                  <!-- Thermometer icon -->
                  <svg class="w-24 h-24 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M9 19c-2.209 0-4-1.791-4-4 0-1.254.588-2.372 1.5-3.097V5.5a2.5 2.5 0 015 0v6.403A3.992 3.992 0 0113 15c0 2.209-1.791 4-4 4z" />
                    <circle cx="9" cy="15" r="2" fill="currentColor" class="animate-pulse" />
                  </svg>
                  <!-- Animated ring -->
                  <div class="absolute inset-0 flex items-center justify-center">
                    <div class="w-20 h-20 border-4 border-blue-500/30 border-t-blue-500 rounded-full animate-spin"></div>
                  </div>
                </div>
                <h3 class="text-lg font-medium text-slate-200 mb-2">Zpracovavam analyzu defrostu</h3>
                <p class="text-slate-400 mb-4">{{ defrostAnalysisProgress.phase }}</p>
                <!-- Progress bar -->
                <div class="w-64 h-2 bg-slate-700 rounded-full overflow-hidden">
                  <div
                    class="h-full bg-gradient-to-r from-blue-500 to-cyan-500 transition-all duration-300"
                    :style="{ width: ((defrostAnalysisProgress.current / defrostAnalysisProgress.total) * 100) + '%' }"
                  ></div>
                </div>
                <p class="text-xs text-slate-500 mt-2">Krok {{ defrostAnalysisProgress.current + 1 }} / {{ defrostAnalysisProgress.total }}</p>
              </div>

              <div v-else class="space-y-4">
                <!-- Multi-log comparison -->
                <div v-if="logsComparison && logsComparison.length > 1" class="bg-slate-800/30 rounded-xl border border-slate-700/50 p-4">
                  <h4 class="text-sm font-medium text-purple-400 border-b border-slate-700 pb-2 mb-3 flex items-center gap-2">
                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    Porovnani souboru
                  </h4>
                  <div class="overflow-x-auto">
                    <table class="w-full text-sm">
                      <thead>
                        <tr class="text-slate-500 text-xs">
                          <th class="text-left pb-2">Soubor</th>
                          <th class="text-center pb-2">Obdobi</th>
                          <th class="text-center pb-2">Delka</th>
                          <th class="text-center pb-2">Defrostu</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="(log, idx) in logsComparison" :key="idx" :class="idx === activeLogIndex ? 'bg-slate-700/30' : ''">
                          <td class="py-1.5">
                            <div class="flex items-center gap-2">
                              <div class="w-3 h-3 rounded-full" :style="{ backgroundColor: log.color }"></div>
                              <span class="text-slate-300 truncate max-w-32" :title="log.fileName">{{ log.fileName }}</span>
                            </div>
                          </td>
                          <td class="text-center text-slate-400 text-xs">{{ formatTime(log.startTime) }} - {{ formatTime(log.endTime) }}</td>
                          <td class="text-center text-slate-300">{{ log.durationHours }}h</td>
                          <td class="text-center text-cyan-400 font-medium">{{ log.defrostCount }}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>

                <!-- Analysis Panel -->
                <div v-if="defrostAnalysis" class="bg-gradient-to-br from-slate-800/50 to-slate-900/50 rounded-xl border border-slate-700/50 p-4">
                  <h4 class="text-sm font-medium text-blue-400 border-b border-slate-700 pb-2 mb-3 flex items-center gap-2">
                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
                    </svg>
                    Analyza defrostu (Sensor Fusion)
                  </h4>

                  <!-- Analysis Parameters -->
                  <div class="grid grid-cols-5 gap-3 mb-4">
                    <div class="bg-slate-800/50 rounded-lg p-2 border border-slate-700/30 text-center">
                      <div class="text-xs text-slate-500">Baseline gap</div>
                      <div class="text-lg font-bold text-blue-400">{{ defrostAnalysis.baselineGap }}K</div>
                      <div class="text-xs text-slate-600">n={{ defrostAnalysis.cleanSamples }}</div>
                    </div>
                    <div class="bg-slate-800/50 rounded-lg p-2 border border-slate-700/30 text-center">
                      <div class="text-xs text-slate-500">K faktor</div>
                      <div class="text-lg font-bold text-purple-400">{{ defrostAnalysis.kFactor }}</div>
                    </div>
                    <div class="bg-slate-800/50 rounded-lg p-2 border border-slate-700/30 text-center">
                      <div class="text-xs text-slate-500">Threshold</div>
                      <div class="text-lg font-bold text-orange-400">{{ defrostAnalysis.gapThreshold }}K</div>
                    </div>
                    <div class="bg-slate-800/50 rounded-lg p-2 border border-slate-700/30 text-center">
                      <div class="text-xs text-slate-500">Baseline COP</div>
                      <div class="text-lg font-bold text-green-400">{{ defrostAnalysis.baselineCop }}</div>
                    </div>
                    <div class="bg-slate-800/50 rounded-lg p-2 border border-slate-700/30 text-center">
                      <div class="text-xs text-slate-500">Avg interval</div>
                      <div class="text-lg font-bold text-cyan-400">{{ defrostAnalysis.summary.avgInterval }}min</div>
                    </div>
                  </div>

                  <!-- Method Comparison -->
                  <div class="grid grid-cols-2 gap-4 mb-4">
                    <!-- Method A: Te Threshold -->
                    <div class="bg-slate-800/30 rounded-lg p-3 border border-amber-500/30">
                      <div class="flex items-center gap-2 mb-2">
                        <div class="w-6 h-6 rounded bg-amber-500/20 flex items-center justify-center text-amber-400 text-xs font-bold">A</div>
                        <span class="text-sm font-medium text-amber-400">Metoda Te threshold</span>
                      </div>
                      <div class="text-xs text-slate-500 mb-2">Absolutni teplota vyparnika</div>
                      <div class="grid grid-cols-3 gap-2 text-center">
                        <div>
                          <div class="text-lg font-bold text-green-400">{{ defrostAnalysis.summary.total - defrostAnalysis.summary.lateCountA - defrostAnalysis.summary.earlyCount }}</div>
                          <div class="text-xs text-slate-500">OK</div>
                        </div>
                        <div>
                          <div class="text-lg font-bold text-yellow-400">{{ defrostAnalysis.summary.earlyCount }}</div>
                          <div class="text-xs text-slate-500">Early</div>
                        </div>
                        <div>
                          <div class="text-lg font-bold text-red-400">{{ defrostAnalysis.summary.lateCountA }}</div>
                          <div class="text-xs text-slate-500">Late</div>
                        </div>
                      </div>
                    </div>

                    <!-- Method B: Sensor Fusion -->
                    <div class="bg-slate-800/30 rounded-lg p-3 border border-cyan-500/30">
                      <div class="flex items-center gap-2 mb-2">
                        <div class="w-6 h-6 rounded bg-cyan-500/20 flex items-center justify-center text-cyan-400 text-xs font-bold">B</div>
                        <span class="text-sm font-medium text-cyan-400">Metoda Sensor Fusion</span>
                      </div>
                      <div class="text-xs text-slate-500 mb-2">Delta T divergence (T_air - Te)</div>
                      <div class="grid grid-cols-3 gap-2 text-center">
                        <div>
                          <div class="text-lg font-bold text-green-400">{{ defrostAnalysis.summary.total - defrostAnalysis.summary.lateCountB }}</div>
                          <div class="text-xs text-slate-500">OK</div>
                        </div>
                        <div>
                          <div class="text-lg font-bold text-red-400">{{ defrostAnalysis.summary.lateCountB }}</div>
                          <div class="text-xs text-slate-500">Late</div>
                        </div>
                        <div>
                          <div class="text-lg font-bold text-orange-400">{{ defrostAnalysis.summary.avgDelayB }}min</div>
                          <div class="text-xs text-slate-500">Avg delay</div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <!-- Conclusions -->
                  <div class="bg-slate-900/50 rounded-lg p-3 border border-slate-700/30">
                    <div class="text-xs font-medium text-slate-400 mb-2">Zavery</div>
                    <div class="space-y-1 text-xs">
                      <div v-if="defrostAnalysis.summary.lateCountB > defrostAnalysis.summary.total / 2" class="flex items-center gap-2 text-red-400">
                        <svg class="w-3 h-3" fill="currentColor" viewBox="0 0 20 20"><path fill-rule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clip-rule="evenodd"/></svg>
                        Vetsina defrostu je pozdnich - doporuceno zkratit interval nebo pouzit adaptivni rizeni
                      </div>
                      <div v-else-if="defrostAnalysis.summary.earlyCount > defrostAnalysis.summary.total / 3" class="flex items-center gap-2 text-yellow-400">
                        <svg class="w-3 h-3" fill="currentColor" viewBox="0 0 20 20"><path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clip-rule="evenodd"/></svg>
                        Mnoho predcasnych defrostu - mozna prodlouzit interval
                      </div>
                      <div v-else class="flex items-center gap-2 text-green-400">
                        <svg class="w-3 h-3" fill="currentColor" viewBox="0 0 20 20"><path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd"/></svg>
                        Defrosty jsou spousteny vcas
                      </div>
                      <div v-if="defrostAnalysis.summary.totalDelayB > 60" class="text-slate-400">
                        Celkove zpozdeni: {{ defrostAnalysis.summary.totalDelayB }} min (potencialni ztrata ucinnosti)
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Summary Stats -->
                <div class="grid grid-cols-4 gap-3">
                  <div class="bg-slate-800/50 rounded-lg p-3 border border-slate-700/30">
                    <div class="text-xs text-slate-500 mb-1">Pocet defrostu</div>
                    <div class="text-xl font-bold text-blue-400">{{ defrostSummary.count }}</div>
                  </div>
                  <div class="bg-slate-800/50 rounded-lg p-3 border border-slate-700/30">
                    <div class="text-xs text-slate-500 mb-1">Prumerna delka</div>
                    <div class="text-xl font-bold text-cyan-400">{{ defrostSummary.avgDuration.toFixed(0) }}s</div>
                  </div>
                  <div class="bg-slate-800/50 rounded-lg p-3 border border-slate-700/30">
                    <div class="text-xs text-slate-500 mb-1">Avg delta T start</div>
                    <div class="text-xl font-bold text-orange-400">{{ defrostSummary.avgDeltaStart?.toFixed(1) || '-' }}K</div>
                  </div>
                  <div class="bg-slate-800/50 rounded-lg p-3 border border-slate-700/30">
                    <div class="text-xs text-slate-500 mb-1">Avg Tc konec</div>
                    <div class="text-xl font-bold text-green-400">{{ defrostSummary.avgTcEnd?.toFixed(1) || '-' }}°C</div>
                  </div>
                </div>

                <!-- Defrost Analysis Table -->
                <div v-if="defrostAnalysis" class="bg-slate-800/30 rounded-xl border border-slate-700/50 overflow-hidden">
                  <div class="overflow-x-auto max-h-[400px] overflow-y-auto">
                    <table class="w-full text-sm">
                      <thead class="bg-slate-900/50 sticky top-0">
                        <tr class="text-slate-400 text-xs">
                          <th class="text-left p-2">#</th>
                          <th class="text-left p-2">Cas</th>
                          <th class="text-center p-2">Te</th>
                          <th class="text-center p-2">T_air</th>
                          <th class="text-center p-2">Gap</th>
                          <th class="text-center p-2">Delka</th>
                          <th class="text-center p-2">Metoda A</th>
                          <th class="text-center p-2">Metoda B</th>
                          <th class="text-center p-2"></th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr
                          v-for="d in defrostAnalysis.defrosts"
                          :key="d.id"
                          class="border-t border-slate-700/30 hover:bg-slate-700/20 cursor-pointer"
                          @click="selectDefrostFromTable(defrosts.find(x => x.id === d.id))"
                        >
                          <td class="p-2 text-cyan-400 font-medium">#{{ d.id }}</td>
                          <td class="p-2 text-slate-300 text-xs">{{ formatTime(d.time) }}</td>
                          <td class="p-2 text-center" :class="d.te < -12 ? 'text-red-400' : d.te < -8 ? 'text-orange-400' : 'text-cyan-400'">{{ d.te?.toFixed(1) }}°C</td>
                          <td class="p-2 text-center text-slate-400">{{ d.tAir?.toFixed(1) }}°C</td>
                          <td class="p-2 text-center" :class="d.gap > defrostAnalysis.gapThreshold ? 'text-orange-400 font-medium' : 'text-slate-400'">{{ d.gap }}K</td>
                          <td class="p-2 text-center text-slate-300">{{ Math.floor(d.duration / 60) }}:{{ String(d.duration % 60).padStart(2, '0') }}</td>
                          <td class="p-2 text-center">
                            <span :class="{
                              'px-2 py-0.5 rounded text-xs font-medium': true,
                              'bg-green-500/20 text-green-400': d.methodA === 'OK',
                              'bg-yellow-500/20 text-yellow-400': d.methodA === 'EARLY',
                              'bg-orange-500/20 text-orange-400': d.methodA === 'LATE',
                              'bg-red-500/20 text-red-400': d.methodA === 'VERY_LATE'
                            }">{{ d.methodA }}</span>
                          </td>
                          <td class="p-2 text-center">
                            <span :class="{
                              'px-2 py-0.5 rounded text-xs font-medium': true,
                              'bg-green-500/20 text-green-400': d.methodB === 'OK',
                              'bg-red-500/20 text-red-400': d.methodB !== 'OK'
                            }">{{ d.methodB }}</span>
                          </td>
                          <td class="p-2 text-center">
                            <button
                              class="p-1 rounded bg-cyan-500/20 hover:bg-cyan-500/30 text-cyan-400"
                              title="Zobrazit v grafu"
                            >
                              <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                              </svg>
                            </button>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>

                <!-- Legacy Defrost List (collapsed by default) -->
                <details class="bg-slate-800/30 rounded-xl border border-slate-700/50">
                  <summary class="p-3 cursor-pointer text-sm text-slate-400 hover:text-slate-200">
                    Detailni seznam defrostu ({{ defrosts.length }})
                  </summary>
                  <div class="space-y-2 p-3 pt-0 max-h-[400px] overflow-y-auto">
                    <div
                      v-for="d in defrosts"
                      :key="d.id"
                      class="bg-slate-800/30 border border-slate-700/30 rounded-lg p-3 hover:border-cyan-500/50 transition-colors cursor-pointer"
                      @click="selectDefrostFromTable(d)"
                    >
                      <div class="flex items-center justify-between mb-2">
                        <div class="flex items-center gap-3">
                          <div class="w-8 h-8 rounded-lg bg-cyan-500/20 flex items-center justify-center">
                            <span class="text-sm font-bold text-cyan-400">#{{ d.id }}</span>
                          </div>
                          <div>
                            <div class="text-sm text-slate-200">{{ formatTime(d.startTime) }}</div>
                            <div class="text-xs text-slate-500">{{ getDefrostReason(d.reason) }}</div>
                          </div>
                        </div>
                        <div class="flex items-center gap-4 text-right">
                          <div>
                            <div class="text-sm font-medium text-slate-200 font-mono">{{ d.durationFormatted }}</div>
                            <div class="text-xs text-slate-500">delka</div>
                          </div>
                          <div>
                            <div class="text-sm font-medium text-orange-400">{{ d.deltaTeAmbient?.start?.toFixed(1) }}K</div>
                            <div class="text-xs text-slate-500">delta T</div>
                          </div>
                          <button
                            class="p-1.5 rounded bg-cyan-500/20 hover:bg-cyan-500/30 text-cyan-400"
                            title="Zobrazit v grafu"
                          >
                            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                            </svg>
                          </button>
                        </div>
                      </div>
                      <!-- Rozsirene info -->
                      <div class="grid grid-cols-4 gap-2 mt-2 pt-2 border-t border-slate-700/30 text-xs">
                        <div>
                          <span class="text-slate-500">Amb:</span>
                          <span class="text-orange-400 ml-1">{{ d.ambient?.start?.toFixed(1) }} / {{ d.ambient?.end?.toFixed(1) }}°C</span>
                        </div>
                        <div>
                          <span class="text-slate-500">Te:</span>
                          <span class="text-cyan-400 ml-1">{{ d.te?.start?.toFixed(1) }} / {{ d.te?.end?.toFixed(1) }}°C</span>
                        </div>
                        <div>
                          <span class="text-slate-500">Tc:</span>
                          <span class="text-green-400 ml-1">{{ d.tc?.start?.toFixed(1) }} / {{ d.tc?.end?.toFixed(1) }}°C</span>
                        </div>
                        <div>
                          <span class="text-slate-500">Te min:</span>
                          <span :class="[(d.te?.min || 0) < -20 ? 'text-red-400' : (d.te?.min || 0) < -10 ? 'text-orange-400' : 'text-cyan-400', 'ml-1']">{{ d.te?.min?.toFixed(1) }}°C</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </details>
              </div>
            </div>

            <!-- Configuration Tab -->
            <div v-else-if="activeTab === 'config'" class="space-y-4">
              <div v-if="!configInfo" class="text-center py-12">
                <svg class="w-12 h-12 mx-auto mb-3 text-slate-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                <p class="text-slate-500">Nahrajte log soubor pro zobrazeni konfigurace</p>
              </div>

              <div v-else class="grid grid-cols-2 gap-4">
                <!-- Log Info -->
                <div class="bg-slate-800/30 rounded-xl border border-slate-700/50 p-4">
                  <h4 class="text-sm font-medium text-blue-400 border-b border-slate-700 pb-2 mb-3 flex items-center gap-2">
                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    Info o logu
                  </h4>
                  <div class="space-y-2 text-sm">
                    <div class="flex justify-between">
                      <span class="text-slate-500">Soubor</span>
                      <span class="text-slate-200 truncate max-w-48" :title="configInfo.logInfo.fileName">{{ configInfo.logInfo.fileName }}</span>
                    </div>
                    <div class="flex justify-between">
                      <span class="text-slate-500">Zacatek</span>
                      <span class="text-slate-200">{{ configInfo.logInfo.startTime }}</span>
                    </div>
                    <div class="flex justify-between">
                      <span class="text-slate-500">Konec</span>
                      <span class="text-slate-200">{{ configInfo.logInfo.endTime }}</span>
                    </div>
                    <div class="flex justify-between">
                      <span class="text-slate-500">Delka zaznamu</span>
                      <span class="text-slate-200">{{ formatDuration(configInfo.logInfo.duration) }}</span>
                    </div>
                    <div class="flex justify-between">
                      <span class="text-slate-500">Pocet vzorku</span>
                      <span class="text-slate-200">{{ configInfo.logInfo.samples?.toLocaleString() }}</span>
                    </div>
                    <div class="flex justify-between">
                      <span class="text-slate-500">Pocet sloupcu</span>
                      <span class="text-slate-200">{{ configInfo.logInfo.columns }}</span>
                    </div>
                  </div>
                </div>

                <!-- Firmware -->
                <div class="bg-slate-800/30 rounded-xl border border-slate-700/50 p-4">
                  <h4 class="text-sm font-medium text-green-400 border-b border-slate-700 pb-2 mb-3 flex items-center gap-2">
                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 19h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2zM9 9h6v6H9V9z" />
                    </svg>
                    Firmware SEC
                  </h4>
                  <div class="space-y-2 text-sm">
                    <div v-if="configInfo.firmware.secRevision !== undefined" class="flex justify-between">
                      <span class="text-slate-500">SEC Revize</span>
                      <span class="text-slate-200">{{ configInfo.firmware.secRevision }}</span>
                    </div>
                    <div v-if="configInfo.firmware.secBuild !== undefined" class="flex justify-between">
                      <span class="text-slate-500">Build</span>
                      <span class="text-slate-200">{{ configInfo.firmware.secBuild }}</span>
                    </div>
                    <div v-if="configInfo.firmware.bootloaderRevision !== undefined" class="flex justify-between">
                      <span class="text-slate-500">Bootloader</span>
                      <span class="text-slate-200">{{ configInfo.firmware.bootloaderRevision }}</span>
                    </div>
                    <div v-if="configInfo.firmware.modbusAddress !== undefined" class="flex justify-between">
                      <span class="text-slate-500">Modbus adresa</span>
                      <span class="text-slate-200">{{ configInfo.firmware.modbusAddress }}</span>
                    </div>
                    <div v-if="configInfo.firmware.applicationMode !== undefined" class="flex justify-between">
                      <span class="text-slate-500">Aplikacni mod</span>
                      <span class="text-slate-200">{{ configInfo.firmware.applicationMode }}</span>
                    </div>
                    <div v-if="configInfo.firmware.refrigerant !== undefined" class="flex justify-between">
                      <span class="text-slate-500">Chladivo</span>
                      <span class="text-slate-200">{{ getRefrigerantName(configInfo.firmware.refrigerant) }}</span>
                    </div>
                  </div>
                </div>

                <!-- VSS Drive -->
                <div class="bg-slate-800/30 rounded-xl border border-slate-700/50 p-4">
                  <h4 class="text-sm font-medium text-purple-400 border-b border-slate-700 pb-2 mb-3 flex items-center gap-2">
                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
                    </svg>
                    VSS Menic
                  </h4>
                  <div class="space-y-2 text-sm">
                    <div v-if="configInfo.vss.driveModel !== undefined" class="flex justify-between">
                      <span class="text-slate-500">Model menice</span>
                      <span class="text-slate-200">{{ configInfo.vss.driveModel }}</span>
                    </div>
                    <div v-if="configInfo.vss.compressorModel !== undefined" class="flex justify-between">
                      <span class="text-slate-500">Model kompresoru</span>
                      <span class="text-slate-200">{{ configInfo.vss.compressorModel }}</span>
                    </div>
                    <div v-if="configInfo.vss.swVersion !== undefined" class="flex justify-between">
                      <span class="text-slate-500">SW verze</span>
                      <span class="text-slate-200">{{ configInfo.vss.swVersion }}</span>
                    </div>
                    <div v-if="configInfo.vss.swRevision !== undefined" class="flex justify-between">
                      <span class="text-slate-500">SW revize</span>
                      <span class="text-slate-200">{{ configInfo.vss.swRevision }}</span>
                    </div>
                    <div v-if="configInfo.vss.eepromRevision !== undefined" class="flex justify-between">
                      <span class="text-slate-500">EEPROM revize</span>
                      <span class="text-slate-200">{{ configInfo.vss.eepromRevision }}</span>
                    </div>
                    <div v-if="configInfo.vss.dspVersion1 !== undefined" class="flex justify-between">
                      <span class="text-slate-500">DSP verze 1</span>
                      <span class="text-slate-200">{{ configInfo.vss.dspVersion1 }}</span>
                    </div>
                    <div v-if="configInfo.vss.dspVersion2 !== undefined" class="flex justify-between">
                      <span class="text-slate-500">DSP verze 2</span>
                      <span class="text-slate-200">{{ configInfo.vss.dspVersion2 }}</span>
                    </div>
                  </div>
                </div>

                <!-- Operating Stats -->
                <div class="bg-slate-800/30 rounded-xl border border-slate-700/50 p-4">
                  <h4 class="text-sm font-medium text-cyan-400 border-b border-slate-700 pb-2 mb-3 flex items-center gap-2">
                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    Provozni citace
                  </h4>
                  <div class="space-y-2 text-sm">
                    <div v-if="configInfo.operating.compressorStarts !== undefined" class="flex justify-between">
                      <span class="text-slate-500">Starty kompresoru</span>
                      <span class="text-slate-200">{{ configInfo.operating.compressorStarts?.toLocaleString() }}</span>
                    </div>
                    <div v-if="configInfo.operating.compressorLifetime !== undefined" class="flex justify-between">
                      <span class="text-slate-500">Doba behu</span>
                      <span class="text-slate-200">{{ formatHours(configInfo.operating.compressorLifetime) }}</span>
                    </div>
                    <div v-if="configInfo.operating.energyTotal !== undefined" class="flex justify-between">
                      <span class="text-slate-500">Celkova spotrebovana energie</span>
                      <span class="text-slate-200">{{ configInfo.operating.energyTotal?.toLocaleString() }} kWh</span>
                    </div>
                    <div v-if="configInfo.operating.heatEnergyTotal !== undefined" class="flex justify-between">
                      <span class="text-slate-500">Celkova vyrobena energie</span>
                      <span class="text-slate-200">{{ configInfo.operating.heatEnergyTotal?.toLocaleString() }} kWh</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </Transition>
</template>

<script setup>
import { ref, computed, watch, onMounted, onUnmounted, nextTick } from 'vue';
import Plotly from 'plotly.js-dist-min';

const props = defineProps({
  isOpen: {
    type: Boolean,
    default: false
  }
});

const emit = defineEmits(['close']);

// Constants
const LOG_COLORS = ['#3b82f6', '#ef4444', '#10b981', '#f59e0b', '#8b5cf6', '#ec4899', '#06b6d4', '#84cc16'];

// Helper pro lokální ISO formát (bez UTC konverze)
const toLocalISOString = (date) => {
  const d = new Date(date);
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  const hours = String(d.getHours()).padStart(2, '0');
  const minutes = String(d.getMinutes()).padStart(2, '0');
  const seconds = String(d.getSeconds()).padStart(2, '0');
  return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}`;
};

// Helper pro parsování lokálního času z Plotly (vrací timestamp v ms)
// Plotly vrací string jako "2025-11-25T20:50:00" v lokálním čase
const parseLocalDateTime = (dateStr) => {
  if (typeof dateStr === 'number') return dateStr;
  if (!dateStr) return 0;

  // Plotly vrací ISO format: "2025-11-25T20:50:00" nebo "2025-11-25 20:50:00"
  // Rozpasujem manuálně abychom se vyhnuli timezone problémům
  const str = dateStr.replace('T', ' ').replace('Z', '');
  const match = str.match(/(\d{4})-(\d{2})-(\d{2})\s+(\d{2}):(\d{2}):(\d{2})/);
  if (match) {
    const [, year, month, day, hour, min, sec] = match;
    // Vytvoř Date objekt s lokálními hodnotami
    return new Date(+year, +month - 1, +day, +hour, +min, +sec).getTime();
  }

  // Fallback
  return new Date(dateStr).getTime();
};

// Helper pro kontrastni barvu textu na pozadi
const getContrastColor = (hexColor) => {
  const hex = hexColor.replace('#', '');
  const r = parseInt(hex.substr(0, 2), 16);
  const g = parseInt(hex.substr(2, 2), 16);
  const b = parseInt(hex.substr(4, 2), 16);
  // Luminance formula
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance > 0.5 ? '#000000' : '#ffffff';
};

const PARAMETER_GROUPS = {
  temperatures: {
    name: 'Teploty',
    color: '#ef4444',
    params: [
      { key: 'T1 Option Sump temp', label: 'T1 Vratka vody (B71)', unit: '°C' },
      { key: 'T2 Option Ambient temp', label: 'T2 Nasavaný vzduch (B91)', unit: '°C' },
      { key: 'T3 Evap suction temp', label: 'T3 Výparník (B84)', unit: '°C' },
      { key: 'T4 Comp suction temp', label: 'T4 Sání kompresoru', unit: '°C' },
      { key: 'T5 Liquid temp 1', label: 'T5 Kapalina 1', unit: '°C' },
      { key: 'T6 Discharge temp', label: 'T6 Výtlak kompresoru', unit: '°C' },
      { key: 'T7 Liquid temp 2', label: 'T7 Výstup vody (B21)', unit: '°C' },
      { key: 'Evap. Temp', label: 'Te Vypařování', unit: '°C' },
      { key: 'Cond. Temp', label: 'Tc Kondenzace', unit: '°C' },
      { key: 'Vss DLT temp', label: 'VSS DLT', unit: '°C' },
      { key: 'Vss DLT maximum', label: 'VSS DLT Max', unit: '°C' },
    ]
  },
  pressures: {
    name: 'Tlaky',
    color: '#3b82f6',
    params: [
      { key: 'P1 Suction pressure', label: 'P1 Suction', unit: 'bar' },
      { key: 'P2 Discharge pressure', label: 'P2 Discharge', unit: 'bar' },
      { key: 'Pressure ratio', label: 'Pressure Ratio', unit: '' },
    ]
  },
  superheat: {
    name: 'Prehrati',
    color: '#f59e0b',
    params: [
      { key: 'Evaporator superheat', label: 'Evap SH', unit: 'K' },
      { key: 'Compressor superheat', label: 'Comp SH', unit: 'K' },
      { key: 'Superheat Setpoint', label: 'SH Setpoint', unit: 'K' },
    ]
  },
  compressor: {
    name: 'Kompresor',
    color: '#8b5cf6',
    params: [
      { key: 'Compressor speed', label: 'Speed', unit: 'RPM' },
      { key: 'Needed speed', label: 'Needed Speed', unit: 'RPM' },
      { key: 'Minimum Speed', label: 'Min Speed', unit: 'RPM' },
      { key: 'Maximum Speed', label: 'Max Speed', unit: 'RPM' },
      { key: 'Vss Torque', label: 'VSS Torque', unit: '%' },
    ]
  },
  valve: {
    name: 'EXV Ventil',
    color: '#10b981',
    params: [
      { key: 'Main Valve Opening', label: 'EXV Opening', unit: '%' },
    ]
  },
  electrical: {
    name: 'Elektrika',
    color: '#eab308',
    params: [
      { key: 'Vss Motor current', label: 'Motor Current', unit: 'A' },
      { key: 'Vss DC Bus Voltage', label: 'DC Bus', unit: 'V' },
      { key: 'Vss AC Line Voltage', label: 'AC Voltage', unit: 'V' },
      { key: 'Vss AC Line Power', label: 'AC Power', unit: 'kW' },
    ]
  },
  capacity: {
    name: 'Vykon & COP',
    color: '#ec4899',
    params: [
      { key: 'Heating capacity', label: 'Heating Cap', unit: 'W' },
      { key: 'Cooling capacity', label: 'Cooling Cap', unit: 'W' },
      { key: 'Heating COP', label: 'Heating COP', unit: '' },
      { key: 'Cooling COP', label: 'Cooling COP', unit: '' },
    ]
  },
  states: {
    name: 'Stavy',
    color: '#6366f1',
    params: [
      { key: 'Application State', label: 'App State', unit: '' },
      { key: 'Heating State', label: 'Heating State', unit: '' },
      { key: 'Cooling State', label: 'Cooling State', unit: '' },
    ]
  }
};

const ALARM_BITS = {
  alarm1: ['Low pressure', 'Low superheat', 'High superheat', 'n/a', 'EVI high SH (warn)', 'Refrigerant loss', 'High cond. pressure', 'Tc low (warn)', 'Tc high (warn)', 'Te low (warn)', 'Te high (warn)', 'Freeze alarm', 'Envelope alarm', 'Defrost by time', 'n/a', 'n/a'],
  hwAlarm1: ['Cooling valve', 'EVI valve', 'n/a', 'P1 sensor', 'P2 sensor', 'P3 sensor', 'n/a', 'n/a', 'n/a', 'T4 sensor', 'T7 sensor (warn)', 'T8 sensor', 'T6 sensor', 'T5 sensor', 'VSS communication', 'n/a'],
  hwAlarm2: ['HP switch', 'VSS locked', 'EEPROM failure', 'Comm timeout', 'Compressor alarm', 'n/a', 'n/a', 'n/a', 'n/a', 'n/a', 'n/a', 'n/a', 'n/a', 'n/a', 'n/a', 'n/a']
};

const APP_STATE_NAMES = {
  0: 'Off', 1: 'Cooling', 2: 'Heating', 3: 'Alarm', 4: 'Trans->Cool', 5: 'Defrost',
  8: 'Trans->Heat', 9: 'Stopping', 10: 'Manual', 11: 'Comp Start', 12: 'EVU Lock', 13: 'Pre-Opening', 14: 'Press Equal'
};

// State
const logs = ref([]);
const activeLogIndex = ref(0);
// Vychozi parametry pro zobrazeni grafu
const DEFAULT_PARAMS = [
  'Compressor speed',           // Speed
  'Main Valve Opening',         // EXV Opening
  'T1 Option Sump temp',        // T1 Vratka vody (B71)
  'T2 Option Ambient temp',     // T2 Nasavaný vzduch (B91)
  'T3 Evap suction temp',       // T3 Výparník (B84)
  'T7 Liquid temp 2',           // T7 Výstup vody (B21)
  'Evap. Temp',                 // Te Vypařování
  'Cond. Temp',                 // Tc Kondenzace
  'Compressor superheat',       // Comp SH
  'Application State'           // App State
];
const selectedParams = ref([...DEFAULT_PARAMS]);
const activeTab = ref('chart');
const paramSearch = ref('');
const expandedGroups = ref(new Set(['temperatures', 'pressures', 'superheat', 'compressor']));
const isDragging = ref(false);
const chartDiv = ref(null);
const lightTheme = ref(false);
const chartNeedsUpdate = ref(false);
const timeRange = ref('3d'); // '1d', '3d', '7d', '30d', 'all'
const isProcessing = ref(false);
const processingProgress = ref({ current: 0, total: 0, fileName: '' });
const showDefrostZones = ref(false);
const selectedDefrost = ref(null);
const currentZoomRange = ref(null); // { start: ms, end: ms, yMin?: number, yMax?: number } - aktualni zoom v grafu
const isRenderingFromZoom = ref(false); // flag pro zabraneni smycky pri zoom renderovani
const defrostAnalysisResult = ref(null); // vysledek async analyzy defrostu
const defrostAnalysisProgress = ref({ processing: false, current: 0, total: 0, phase: '' });
const savedUserParams = ref(null); // ulozene uzivatelske parametry pred defrost modem

// Vychozi parametry pro defrost analyzu
const DEFROST_PARAMS = [
  'Compressor speed',
  'Main Valve Opening',
  'T1 Option Sump temp',      // T1 Vratka vody (B71)
  'T2 Option Ambient temp',   // T2 Nasavaný vzduch (B91)
  'T3 Evap suction temp',     // T3 Výparník (B84)
  'T7 Liquid temp 2',         // T7 Výstup vody (B21)
  'Evap. Temp',               // Te Vypařování
  'Cond. Temp',               // Tc Kondenzace
  'Application State'         // App State
];

// Aktivuje defrost mod a nastavi defrost parametry (pokud jeste nejsou)
const activateDefrostMode = () => {
  if (!showDefrostZones.value) {
    // Ulozit aktualni parametry a nastavit defrost parametry
    savedUserParams.value = [...selectedParams.value];
    selectedParams.value = DEFROST_PARAMS.filter(p => {
      const log = logs.value[activeLogIndex.value];
      return log?.data?.[0]?.[p] !== undefined;
    });
    showDefrostZones.value = true;
  }
};

// Toggle defrost mode - ulozi/obnovi uzivatelske parametry
const toggleDefrostMode = () => {
  if (!showDefrostZones.value) {
    activateDefrostMode();
  } else {
    // Vypnuti defrost modu - obnovit ulozene parametry
    if (savedUserParams.value) {
      selectedParams.value = savedUserParams.value;
      savedUserParams.value = null;
    }
    showDefrostZones.value = false;
  }
  selectedDefrost.value = null;
  renderChart();
};

// Kliknuti na defrost v tabulce - aktivuje defrost mod a zobrazi detail
const selectDefrostFromTable = (defrost) => {
  activateDefrostMode();
  selectedDefrost.value = defrost;
  currentZoomRange.value = null; // Reset manualni zoom - defrost zoom ma prednost
  activeTab.value = 'chart';
  nextTick(() => renderChart());
};

const TIME_RANGES = [
  { key: '1d', label: '1 den' },
  { key: '3d', label: '3 dny' },
  { key: '7d', label: 'Tyden' },
  { key: '30d', label: 'Mesic' },
  { key: 'all', label: 'Vse' }
];


const tabs = [
  { id: 'chart', label: 'Graf' },
  { id: 'analysis', label: 'Analyza' },
  { id: 'diagnostics', label: 'Diagnostika' },
  { id: 'defrosts', label: 'Defr.Anal.' },
  { id: 'config', label: 'Konfigurace' }
];

// Computed
const activeLog = computed(() => logs.value[activeLogIndex.value]);

const progressPercent = computed(() => {
  if (processingProgress.value.total === 0) return 0;
  return (processingProgress.value.current / processingProgress.value.total) * 100;
});

const availableParams = computed(() => {
  if (logs.value.length === 0) return [];
  const allParams = new Set();
  logs.value.forEach(log => {
    log.headers.forEach(h => {
      if (h !== 'Date' && !h.includes('_hex')) {
        allParams.add(h);
      }
    });
  });
  return Array.from(allParams);
});

const filteredGroups = computed(() => {
  const result = {};
  const knownParams = new Set();

  // Projdi definovane skupiny a pridej parametry ktere existuji v souboru
  Object.entries(PARAMETER_GROUPS).forEach(([key, group]) => {
    const filteredParams = group.params.filter(p => {
      if (availableParams.value.includes(p.key)) {
        knownParams.add(p.key);
        return (p.label.toLowerCase().includes(paramSearch.value.toLowerCase()) ||
                p.key.toLowerCase().includes(paramSearch.value.toLowerCase()));
      }
      return false;
    });
    if (filteredParams.length > 0) {
      result[key] = { ...group, params: filteredParams };
    }
  });

  // Pridej skupinu "Ostatni" pro parametry ze souboru ktere nejsou v PARAMETER_GROUPS
  const unknownParams = availableParams.value.filter(p =>
    !knownParams.has(p) &&
    p !== 'Date' &&
    !p.includes('_hex') &&
    (p.toLowerCase().includes(paramSearch.value.toLowerCase()))
  );

  if (unknownParams.length > 0) {
    result.other = {
      name: 'Ostatní',
      color: '#6b7280',
      params: unknownParams.map(key => ({
        key,
        label: key,
        unit: ''
      }))
    };
  }

  return result;
});

const chartData = computed(() => {
  const visibleLogs = logs.value.filter(l => l.visible !== false);
  if (visibleLogs.length === 0) return [];
  const baseLog = visibleLogs[0];
  return baseLog.data.map((row, index) => {
    const point = { time: formatTime(row.Date), _index: index };
    selectedParams.value.forEach(param => {
      point[param] = row[param];
    });
    return point;
  });
});

const stats = computed(() => {
  if (!activeLog.value) return null;
  const data = activeLog.value.data;
  const runningStates = [1, 2, 4, 5, 8, 11];
  const runningData = data.filter(d => runningStates.includes(d['Application State']) && d['Compressor speed'] > 0);
  const heatingData = data.filter(d => d['Application State'] === 2 && d['Compressor speed'] > 0);
  const coolingData = data.filter(d => d['Application State'] === 1 && d['Compressor speed'] > 0);
  const defrostData = data.filter(d => (d['Application State'] === 4 || d['Application State'] === 5));

  const calcStats = (dataset, key) => {
    const values = dataset.map(d => d[key]).filter(v => v !== undefined && !isNaN(v));
    if (values.length === 0) return { min: 0, max: 0, avg: 0 };
    return {
      min: Math.min(...values),
      max: Math.max(...values),
      avg: values.reduce((a, b) => a + b, 0) / values.length
    };
  };

  return {
    totalSamples: data.length,
    runningSamples: runningData.length,
    runningPercentage: (runningData.length / data.length * 100),
    duration: activeLog.value.duration,
    heatingTime: heatingData.length,
    coolingTime: coolingData.length,
    defrostTime: defrostData.length,
    heatingPercentage: (heatingData.length / data.length * 100),
    coolingPercentage: (coolingData.length / data.length * 100),
    defrostPercentage: (defrostData.length / data.length * 100),
    speed: calcStats(runningData, 'Compressor speed'),
    superheat: calcStats(runningData, 'Compressor superheat'),
    exv: calcStats(runningData, 'Main Valve Opening'),
    dlt: calcStats(runningData, 'Vss DLT temp'),
    te: calcStats(runningData, 'Evap. Temp'),
    tc: calcStats(runningData, 'Cond. Temp'),
    cop: calcStats(heatingData, 'Heating COP'),
    power: calcStats(runningData, 'Vss AC Line Power'),
  };
});

const alarms = computed(() => {
  if (!activeLog.value) return { software: [], hardware: [] };
  const software = new Set();
  const hardware = new Set();
  activeLog.value.data.forEach(row => {
    parseAlarmBits(row['Alarm 1'] || 0, ALARM_BITS.alarm1).forEach(a => software.add(a.name));
    parseAlarmBits(row['Hardware alarm 1'] || 0, ALARM_BITS.hwAlarm1).forEach(a => hardware.add(a.name));
    parseAlarmBits(row['Hardware alarm 2'] || 0, ALARM_BITS.hwAlarm2).forEach(a => hardware.add(a.name));
  });
  return { software: Array.from(software), hardware: Array.from(hardware) };
});

const defrosts = computed(() => {
  if (!activeLog.value) return [];
  const data = activeLog.value.data;
  const result = [];
  let inDefrost = false;
  let defrostStart = null;
  let defrostData = [];

  data.forEach((row, index) => {
    const appState = row['Application State'];
    const isDefrost = appState === 4 || appState === 5;

    if (isDefrost && !inDefrost) {
      inDefrost = true;
      defrostStart = { index, time: row.Date, data: row };
      defrostData = [row];
    } else if (isDefrost && inDefrost) {
      defrostData.push(row);
    } else if (!isDefrost && inDefrost) {
      inDefrost = false;
      const startData = defrostStart.data;
      const endData = row;

      // Teploty na zacatku a konci
      const startAmbient = startData['T2 Option Ambient temp'];
      const endAmbient = endData['T2 Option Ambient temp'];
      const startEvapSuction = startData['T3 Evap. suction temp'];
      const endEvapSuction = endData['T3 Evap. suction temp'];
      const startTe = startData['Evap. Temp'];
      const endTe = endData['Evap. Temp'];
      const startTc = startData['Cond. Temp'];
      const endTc = endData['Cond. Temp'];

      // Min/max Te behem defrostu
      const teValues = defrostData.map(r => r['Evap. Temp']).filter(v => v !== undefined && v !== null);
      const teMin = teValues.length ? Math.min(...teValues) : null;
      const teMax = teValues.length ? Math.max(...teValues) : null;

      // Otacky kompresoru a EXV
      const speedValues = defrostData.map(r => r['Compressor Speed']).filter(v => v !== undefined && v !== null);
      const speedMin = speedValues.length ? Math.min(...speedValues) : null;
      const speedMax = speedValues.length ? Math.max(...speedValues) : null;

      const exvValues = defrostData.map(r => r['Spool Position (%)']).filter(v => v !== undefined && v !== null);
      const exvMin = exvValues.length ? Math.min(...exvValues) : null;
      const exvMax = exvValues.length ? Math.max(...exvValues) : null;

      // Vypocet skutecneho casoveho intervalu vzorkovani
      let sampleIntervalSec = 1; // default 1s
      if (defrostData.length >= 2) {
        const t1 = new Date(defrostData[0].Date).getTime();
        const t2 = new Date(defrostData[1].Date).getTime();
        sampleIntervalSec = Math.max(1, Math.round((t2 - t1) / 1000));
      }

      // Cas pod prahy Te (s korekci na skutecny interval vzorkovani)
      const timeBelow = {
        minus10: defrostData.filter(r => (r['Evap. Temp'] || 0) < -10).length * sampleIntervalSec,
        minus15: defrostData.filter(r => (r['Evap. Temp'] || 0) < -15).length * sampleIntervalSec,
        minus20: defrostData.filter(r => (r['Evap. Temp'] || 0) < -20).length * sampleIntervalSec,
        minus25: defrostData.filter(r => (r['Evap. Temp'] || 0) < -25).length * sampleIntervalSec,
      };

      // Delka ze skutecneho casu (ne z poctu radku)
      const startTimeMs = new Date(defrostStart.time).getTime();
      const endTimeMs = new Date(row.Date).getTime();
      const durationSec = Math.round((endTimeMs - startTimeMs) / 1000);
      const durationFormatted = `${Math.floor(durationSec / 60)}:${String(durationSec % 60).padStart(2, '0')}`;

      const deltaStart = (startAmbient || 0) - (startTe || 0);

      result.push({
        id: result.length + 1,
        startIndex: defrostStart.index,
        endIndex: index,
        startTime: defrostStart.time,
        endTime: row.Date,
        duration: durationSec,
        durationFormatted,
        reason: startData['Defrost reason'],
        // Info o vzorkovani
        sampleCount: defrostData.length,
        sampleInterval: sampleIntervalSec,
        // Teploty
        ambient: { start: startAmbient, end: endAmbient },
        evapSuction: { start: startEvapSuction, end: endEvapSuction },
        te: { start: startTe, end: endTe, min: teMin, max: teMax },
        tc: { start: startTc, end: endTc },
        // Provozni hodnoty
        compressorSpeed: { min: speedMin, max: speedMax },
        exv: { min: exvMin, max: exvMax },
        // Statistiky Te pod prahy
        timeBelow,
        // Zpetna kompatibilita
        startTc,
        endTc,
        deltaTeAmbient: { start: deltaStart }
      });
      defrostData = [];
    }
  });
  return result;
});

const defrostSummary = computed(() => {
  if (defrosts.value.length === 0) return { count: 0, avgDuration: 0 };
  const durations = defrosts.value.map(d => d.duration);
  const deltaStarts = defrosts.value.map(d => d.deltaTeAmbient?.start).filter(v => v !== undefined && !isNaN(v));
  const tcEnds = defrosts.value.map(d => d.endTc).filter(v => v !== undefined);
  return {
    count: defrosts.value.length,
    avgDuration: durations.reduce((a, b) => a + b, 0) / durations.length,
    avgDeltaStart: deltaStarts.length ? deltaStarts.reduce((a, b) => a + b, 0) / deltaStarts.length : null,
    avgTcEnd: tcEnds.length ? tcEnds.reduce((a, b) => a + b, 0) / tcEnds.length : null,
  };
});

// Defrost analysis - returns cached result (computed by runDefrostAnalysis)
const defrostAnalysis = computed(() => defrostAnalysisResult.value);

// Async defrost analysis using both methodologies (Te threshold + Sensor Fusion)
const runDefrostAnalysis = async () => {
  if (!activeLog.value || defrosts.value.length === 0) {
    defrostAnalysisResult.value = null;
    return;
  }

  defrostAnalysisProgress.value = { processing: true, current: 0, total: 3, phase: 'Vypocet baseline...' };
  await nextTick(); // Allow UI to update

  const data = activeLog.value.data;
  const STABILIZE_TIME_SEC = 600; // 10 min stabilization after defrost
  const K_FACTOR = 2.0; // Measured sensitivity factor

  // Phase 1: Calculate baseline gap (clean coil, Te > -6°C, heating mode)
  let cleanGapSum = 0;
  let cleanGapCount = 0;
  let cleanCopSum = 0;
  let cleanCopCount = 0;

  // Process in chunks to keep UI responsive
  const CHUNK_SIZE = 5000;
  for (let chunk = 0; chunk < data.length; chunk += CHUNK_SIZE) {
    const endIdx = Math.min(chunk + CHUNK_SIZE, data.length);
    for (let i = chunk; i < endIdx; i++) {
      const row = data[i];
      const state = row['Application State'];
      const te = row['Evap. Temp'];
      const tAir = row['T2 Option Ambient temp'];
      const cop = row['Heating COP'];

      if (state === 2 && te > -6 && tAir !== undefined && te !== undefined) {
        const gap = tAir - te;
        if (gap > 0 && gap < 20) { // Sanity check
          cleanGapSum += gap;
          cleanGapCount++;
          if (cop > 0 && cop < 10) {
            cleanCopSum += cop;
            cleanCopCount++;
          }
        }
      }
    }
    // Yield to UI every chunk
    if (chunk + CHUNK_SIZE < data.length) {
      await new Promise(r => setTimeout(r, 0));
    }
  }

  const baselineGap = cleanGapCount > 0 ? cleanGapSum / cleanGapCount : 5.4;
  const baselineCop = cleanCopCount > 0 ? cleanCopSum / cleanCopCount : 3.0;
  const gapThreshold = baselineGap * K_FACTOR;

  // Phase 2: Pre-process data for faster lookup
  defrostAnalysisProgress.value = { processing: true, current: 1, total: 3, phase: 'Priprava dat...' };
  await nextTick();

  // Build defrost end times for stabilization check
  const defrostEndTimes = {};
  defrosts.value.forEach((d, idx) => {
    defrostEndTimes[idx] = new Date(d.endTime).getTime();
  });

  // Pre-compute row times and prepare index for faster binary search
  const rowTimes = [];
  for (let i = 0; i < data.length; i++) {
    rowTimes.push(new Date(data[i].Date).getTime());
  }

  // Binary search helper
  const findFirstIndexAfter = (targetTime) => {
    let low = 0, high = rowTimes.length - 1;
    while (low < high) {
      const mid = Math.floor((low + high) / 2);
      if (rowTimes[mid] < targetTime) low = mid + 1;
      else high = mid;
    }
    return low;
  };

  // Phase 3: Analyze each defrost
  defrostAnalysisProgress.value = { processing: true, current: 2, total: 3, phase: 'Analyza defrostu...' };
  await nextTick();

  const analyzed = [];
  for (let idx = 0; idx < defrosts.value.length; idx++) {
    const d = defrosts.value[idx];
    const startTime = new Date(d.startTime).getTime();
    const te = d.te?.start || 0;
    const tAir = d.ambient?.start || 0;
    const gap = tAir - te;

    // Method A: Te threshold assessment
    let methodA = 'OK';
    if (te > -8) methodA = 'EARLY';
    else if (te < -13) methodA = 'VERY_LATE';
    else if (te < -10) methodA = 'LATE';

    // Method B: Find when gap threshold was exceeded (after stabilization)
    let optimalTime = null;
    let delay = 0;

    // Get previous defrost end time
    const prevDefrostEnd = idx > 0 ? defrostEndTimes[idx - 1] : 0;
    const stabilizeEnd = prevDefrostEnd + STABILIZE_TIME_SEC * 1000;

    // Use binary search to find starting index
    const startIdx = findFirstIndexAfter(stabilizeEnd);

    // Search data for when threshold was exceeded
    for (let i = startIdx; i < data.length; i++) {
      const rowTime = rowTimes[i];
      if (rowTime >= startTime) break;

      const row = data[i];
      const state = row['Application State'];
      if (state !== 2) continue; // Only heating mode

      const rowTe = row['Evap. Temp'];
      const rowTAir = row['T2 Option Ambient temp'];
      if (rowTe === undefined || rowTAir === undefined) continue;

      const rowGap = rowTAir - rowTe;
      if (rowGap >= gapThreshold) {
        optimalTime = rowTime;
        break;
      }
    }

    if (optimalTime) {
      delay = Math.round((startTime - optimalTime) / 60000); // minutes
    }

    const methodB = optimalTime ? (delay > 0 ? `${delay} min` : 'OK') : 'OK';

    analyzed.push({
      id: d.id,
      time: d.startTime,
      te,
      tAir,
      gap: Math.round(gap * 10) / 10,
      duration: d.duration,
      methodA,
      methodB,
      delay,
      isLateA: methodA === 'LATE' || methodA === 'VERY_LATE',
      isLateB: optimalTime !== null && delay > 0,
      isEarly: methodA === 'EARLY' && !optimalTime
    });

    // Yield to UI between defrosts
    if (idx % 3 === 0) {
      await new Promise(r => setTimeout(r, 0));
    }
  }

  // Calculate summary stats
  const lateCountA = analyzed.filter(a => a.isLateA).length;
  const lateCountB = analyzed.filter(a => a.isLateB).length;
  const earlyCount = analyzed.filter(a => a.isEarly).length;
  const totalDelayB = analyzed.reduce((sum, a) => sum + (a.isLateB ? a.delay : 0), 0);
  const avgDelayB = lateCountB > 0 ? Math.round(totalDelayB / lateCountB) : 0;

  // Calculate intervals
  const intervals = [];
  for (let i = 1; i < defrosts.value.length; i++) {
    const prev = new Date(defrosts.value[i - 1].startTime).getTime();
    const curr = new Date(defrosts.value[i].startTime).getTime();
    intervals.push(Math.round((curr - prev) / 60000));
  }
  const avgInterval = intervals.length > 0 ? Math.round(intervals.reduce((a, b) => a + b, 0) / intervals.length) : 0;

  defrostAnalysisResult.value = {
    baselineGap: Math.round(baselineGap * 100) / 100,
    baselineCop: Math.round(baselineCop * 100) / 100,
    kFactor: K_FACTOR,
    gapThreshold: Math.round(gapThreshold * 10) / 10,
    cleanSamples: cleanGapCount,
    defrosts: analyzed,
    summary: {
      total: analyzed.length,
      earlyCount,
      lateCountA,
      lateCountB,
      totalDelayB,
      avgDelayB,
      avgInterval
    }
  };

  defrostAnalysisProgress.value = { processing: false, current: 3, total: 3, phase: 'Hotovo' };
};

// Multi-log comparison
const logsComparison = computed(() => {
  if (logs.value.length < 2) return null;

  return logs.value.map((log, idx) => {
    // Calculate stats for each log
    const data = log.data;
    let defrostCount = 0;
    let inDefrost = false;

    data.forEach(row => {
      const state = row['Application State'];
      if ((state === 4 || state === 5) && !inDefrost) {
        inDefrost = true;
        defrostCount++;
      } else if (state !== 4 && state !== 5) {
        inDefrost = false;
      }
    });

    // Time range
    const startTime = data[0]?.Date;
    const endTime = data[data.length - 1]?.Date;
    const durationHours = startTime && endTime
      ? Math.round((new Date(endTime) - new Date(startTime)) / 3600000 * 10) / 10
      : 0;

    return {
      fileName: log.fileName,
      color: LOG_COLORS[idx % LOG_COLORS.length],
      startTime,
      endTime,
      durationHours,
      samples: data.length,
      defrostCount
    };
  });
});

const diagnostics = computed(() => {
  if (!activeLog.value || !stats.value) return null;
  const scores = { heating: 100, defrost: 100, reliability: 100, efficiency: 100 };

  // Heating score
  if (stats.value.superheat.avg < 4) scores.heating -= 30;
  else if (stats.value.superheat.avg > 20) scores.heating -= 20;
  if (stats.value.dlt.max > 135) scores.heating -= 20;

  // Defrost score
  if (defrosts.value.length > 0) {
    const longDefrosts = defrosts.value.filter(d => d.duration > 600).length;
    scores.defrost -= longDefrosts * 10;
  }

  // Reliability
  scores.reliability -= Math.min(50, alarms.value.software.length * 5);
  scores.reliability -= Math.min(50, alarms.value.hardware.length * 10);

  // Efficiency
  if (stats.value.cop.avg < 2.0) scores.efficiency -= 40;
  else if (stats.value.cop.avg < 2.5) scores.efficiency -= 20;
  else if (stats.value.cop.avg >= 4.0) scores.efficiency += 10;

  // Clamp
  Object.keys(scores).forEach(key => {
    scores[key] = Math.max(0, Math.min(100, scores[key]));
  });
  scores.overall = Math.round((scores.heating + scores.defrost + scores.reliability + scores.efficiency) / 4);

  return { scores };
});

const scoreItems = computed(() => {
  if (!diagnostics.value) return [];
  return [
    { label: 'Topeni', score: diagnostics.value.scores.heating },
    { label: 'Defrost', score: diagnostics.value.scores.defrost },
    { label: 'Spolehlivost', score: diagnostics.value.scores.reliability },
    { label: 'Ucinnost', score: diagnostics.value.scores.efficiency },
  ];
});

const issues = computed(() => {
  if (!stats.value || !diagnostics.value) return [];
  const result = [];

  // Low superheat
  if (stats.value.superheat.avg < 4) {
    result.push({
      severity: 'critical',
      category: 'Superheat',
      title: 'Nizke prehrati - riziko zaplaveni kompresoru',
      description: `Prumerne prehrati ${stats.value.superheat.avg.toFixed(1)}K je pod bezpecnou hranici.`,
      recommendation: 'Zkontrolujte napln chladiva, funkci EXV, pripadne bypass ventilu.'
    });
  }

  // High DLT
  if (stats.value.dlt.max > 130) {
    result.push({
      severity: 'critical',
      category: 'DLT',
      title: 'Vysoka teplota vytlaku',
      description: `DLT dosahla ${stats.value.dlt.max.toFixed(1)}°C. Hranice je 135°C.`,
      recommendation: 'Zkontrolujte prutok kondenzatorem, funkci ventilatoru/cerpadla.'
    });
  }

  // Hardware alarms
  if (alarms.value.hardware.length > 0) {
    result.push({
      severity: 'critical',
      category: 'Hardware',
      title: 'Hardware alarmy',
      description: `Aktivni hardware alarmy: ${alarms.value.hardware.join(', ')}`,
      recommendation: 'Zkontrolujte cidla, kabelaz a konektory.'
    });
  }

  // Low COP
  if (stats.value.cop.avg > 0 && stats.value.cop.avg < 2.5) {
    result.push({
      severity: 'warning',
      category: 'Ucinnost',
      title: 'Nizky COP',
      description: `Prumerny COP ${stats.value.cop.avg.toFixed(2)} je pod optimalni hodnotou.`,
      recommendation: 'Proverte provozni podminky, cistotu vymenniku, spravnost naplne.'
    });
  }

  // Good COP
  if (stats.value.cop.avg >= 3.5) {
    result.push({
      severity: 'success',
      category: 'Ucinnost',
      title: 'Vysoky COP',
      description: `Prumerny COP ${stats.value.cop.avg.toFixed(2)} je vyborny.`,
      recommendation: 'System pracuje efektivne.'
    });
  }

  return result.sort((a, b) => {
    const order = { critical: 0, warning: 1, info: 2, success: 3 };
    return order[a.severity] - order[b.severity];
  });
});

// Configuration info extracted from log
const configInfo = computed(() => {
  if (!activeLog.value || !activeLog.value.data || activeLog.value.data.length === 0) return null;

  // Get second row for config values (first row often has invalid data)
  const dataRow = activeLog.value.data.length > 1 ? activeLog.value.data[1] : activeLog.value.data[0];
  const headers = activeLog.value.headers || [];

  // Helper to get value if header exists
  const getValue = (key) => {
    if (headers.includes(key)) {
      return dataRow[key];
    }
    return undefined;
  };

  // Firmware & System
  const firmware = {
    secRevision: getValue('SEC Revision'),
    secBuild: getValue('Build number'),
    bootloaderRevision: getValue('Boot loader Revision'),
    modbusAddress: getValue('Modbus address'),
    applicationMode: getValue('Application mode'),
    refrigerant: getValue('Refrigerant type')
  };

  // VSS Drive Info
  const vss = {
    eepromRevision: getValue('VSS EEPROM rev'),
    dspVersion1: getValue('Vss DSP version 1'),
    dspVersion2: getValue('Vss DSP version 2'),
    driveModel: getValue('Vss Drive Model Number'),
    compressorModel: getValue('Vss Compressor Model Number'),
    swVersion: getValue('Vss SW Version'),
    swRevision: getValue('Vss SW Revision')
  };

  // Compressor & Operating - static counters (use last row for most recent values)
  const lastRow = activeLog.value.data[activeLog.value.data.length - 1];
  const getLastValue = (key) => headers.includes(key) ? lastRow[key] : undefined;

  const operating = {
    compressorStarts: getLastValue('CompressorStarts'),
    compressorLifetime: getLastValue('Compressor lifetime'),
    energyTotal: getLastValue('Energy total'),
    heatEnergyTotal: getLastValue('Heat Energy total')
  };

  // Log info
  const logInfo = {
    fileName: activeLog.value.fileName,
    startTime: activeLog.value.startTime,
    endTime: activeLog.value.endTime,
    duration: activeLog.value.duration,
    samples: activeLog.value.data.length,
    columns: headers.length
  };

  return {
    firmware,
    vss,
    operating,
    logInfo
  };
});

// Methods
const close = () => emit('close');

const parseLogFile = async (content, fileName) => {
  const lines = content.trim().split('\n');
  if (lines.length < 2) return null;
  const headers = lines[0].split('\t');
  const data = [];
  const totalLines = lines.length - 1;

  // Update progress
  processingProgress.value = { current: 0, total: totalLines, fileName };

  // Process in chunks to allow UI updates
  const CHUNK_SIZE = 5000;

  for (let i = 1; i < lines.length; i++) {
    const values = lines[i].split('\t');
    if (values.length !== headers.length) continue;
    const row = { _index: i - 1 };
    headers.forEach((header, idx) => {
      const value = values[idx];
      if (header === 'Date') {
        row[header] = value;
        // Parsuj SEC log format: "MM/DD/YYYY HH:MM:SS" jako lokalni cas
        const match = value.match(/(\d{1,2})\/(\d{1,2})\/(\d{4})\s+(\d{1,2}):(\d{2}):(\d{2})/);
        if (match) {
          const [, month, day, year, hour, min, sec] = match;
          row._timestamp = new Date(+year, +month - 1, +day, +hour, +min, +sec).getTime();
        } else {
          row._timestamp = new Date(value).getTime();
        }
      } else if (value.startsWith('0x')) {
        row[header] = parseInt(value, 16);
      } else {
        row[header] = parseFloat(value) || 0;
      }
    });
    data.push(row);

    // Update progress every chunk
    if (i % CHUNK_SIZE === 0 || i === lines.length - 1) {
      processingProgress.value = { current: i, total: totalLines, fileName };
      // Yield to UI thread
      await new Promise(resolve => setTimeout(resolve, 0));
    }
  }

  return {
    fileName,
    headers,
    data,
    visible: true,
    startTime: data[0]?.Date,
    endTime: data[data.length - 1]?.Date,
    duration: data.length > 1 ? (data[data.length - 1]._timestamp - data[0]._timestamp) / 1000 : 0
  };
};

const handleFileUpload = async (e) => {
  const files = Array.from(e.target.files);
  isProcessing.value = true;

  for (const file of files) {
    try {
      const content = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (event) => resolve(event.target.result);
        reader.onerror = reject;
        reader.readAsText(file);
      });

      const parsed = await parseLogFile(content, file.name);
      if (parsed) {
        logs.value.push(parsed);
      }
    } catch (error) {
      console.error('Error reading file:', file.name, error);
    }
  }

  isProcessing.value = false;
  processingProgress.value = { current: 0, total: 0, fileName: '' };
  e.target.value = '';
};

const removeLog = (index) => {
  logs.value.splice(index, 1);
  if (activeLogIndex.value >= logs.value.length && logs.value.length > 0) {
    activeLogIndex.value = logs.value.length - 1;
  }
};

const toggleParam = (param) => {
  const idx = selectedParams.value.indexOf(param);
  if (idx >= 0) {
    selectedParams.value.splice(idx, 1);
  } else {
    selectedParams.value.push(param);
  }
};

const setDefaultParams = () => {
  selectedParams.value = [...DEFAULT_PARAMS];
};

const toggleGroup = (groupKey) => {
  if (expandedGroups.value.has(groupKey)) {
    expandedGroups.value.delete(groupKey);
  } else {
    expandedGroups.value.add(groupKey);
  }
};

const countSelectedInGroup = (group) => {
  return group.params.filter(p => selectedParams.value.includes(p.key)).length;
};

const getParamLabel = (key) => {
  for (const group of Object.values(PARAMETER_GROUPS)) {
    const param = group.params.find(p => p.key === key);
    if (param) return param.label;
  }
  return key;
};

const parseAlarmBits = (value, definitions) => {
  const alarms = [];
  for (let i = 0; i < 16; i++) {
    if ((value >> i) & 1) {
      if (definitions[i] && definitions[i] !== 'n/a') {
        alarms.push({ bit: i, name: definitions[i] });
      }
    }
  }
  return alarms;
};

const formatDuration = (seconds) => {
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = Math.floor(seconds % 60);
  return `${h}h ${m}m ${s}s`;
};

const formatTime = (dateStr) => {
  if (!dateStr) return '';
  const parts = dateStr.split(' ');
  return parts[1] || dateStr;
};

const getDefrostReason = (reason) => {
  const reasons = { 0: 'Zadny', 1: 'Casovy interval', 2: 'Delta trigger', 3: 'Absolutni trigger', 4: 'Externi pozadavek', 5: 'DI1 trigger' };
  return reasons[reason] || `Neznamy (${reason})`;
};

const formatSeconds = (seconds) => {
  if (!seconds || seconds === 0) return '-';
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  if (mins > 0) {
    return `${mins}:${String(secs).padStart(2, '0')}`;
  }
  return `${secs}s`;
};

const getRefrigerantName = (code) => {
  const refrigerants = {
    0: 'R410A',
    1: 'R407C',
    2: 'R134a',
    3: 'R404A',
    4: 'R507',
    5: 'R22',
    6: 'R290',
    7: 'R32',
    8: 'R454B',
    9: 'R1234yf',
    10: 'R1234ze',
    11: 'R452B'
  };
  return refrigerants[code] || `Neznamy (${code})`;
};

const formatHours = (hours) => {
  if (!hours && hours !== 0) return '-';
  const h = Math.floor(hours);
  const d = Math.floor(h / 24);
  const remainingH = h % 24;
  if (d > 0) {
    return `${d.toLocaleString()} dnu, ${remainingH}h`;
  }
  return `${h}h`;
};

const getScoreColor = (score) => {
  if (score >= 85) return 'text-green-400';
  if (score >= 70) return 'text-yellow-400';
  if (score >= 50) return 'text-orange-400';
  return 'text-red-400';
};

const getScoreBg = (score) => {
  if (score >= 85) return 'bg-green-500/20 border-green-500/30';
  if (score >= 70) return 'bg-yellow-500/20 border-yellow-500/30';
  if (score >= 50) return 'bg-orange-500/20 border-orange-500/30';
  return 'bg-red-500/20 border-red-500/30';
};

const getScoreBarColor = (score) => {
  if (score >= 85) return 'bg-green-500';
  if (score >= 70) return 'bg-yellow-500';
  if (score >= 50) return 'bg-orange-500';
  return 'bg-red-500';
};

const getSeverityStyles = (severity) => {
  switch (severity) {
    case 'critical': return 'bg-red-500/10 border-red-500/30 text-red-300';
    case 'warning': return 'bg-orange-500/10 border-orange-500/30 text-orange-300';
    case 'info': return 'bg-blue-500/10 border-blue-500/30 text-blue-300';
    case 'success': return 'bg-green-500/10 border-green-500/30 text-green-300';
    default: return 'bg-slate-500/10 border-slate-500/30 text-slate-300';
  }
};

const getSeverityIcon = (severity) => {
  switch (severity) {
    case 'critical': return '🔴';
    case 'warning': return '🟠';
    case 'info': return '🔵';
    case 'success': return '🟢';
    default: return '⚪';
  }
};

// Drag & Drop handlers
const handleDragOver = (e) => {
  e.preventDefault();
  isDragging.value = true;
};

const handleDragLeave = (e) => {
  e.preventDefault();
  isDragging.value = false;
};

const handleDrop = (e) => {
  e.preventDefault();
  isDragging.value = false;
  const allFiles = Array.from(e.dataTransfer.files).filter(f =>
    f.name.endsWith('.txt') || f.name.endsWith('.csv') || f.name.endsWith('.log')
  );
  // Povolit pouze 1 soubor
  const files = allFiles.slice(0, 1);
  processFiles(files);
};

const processFiles = async (files) => {
  isProcessing.value = true;

  for (const file of files) {
    try {
      const content = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (event) => resolve(event.target.result);
        reader.onerror = reject;
        reader.readAsText(file);
      });

      const parsed = await parseLogFile(content, file.name);
      if (parsed) {
        logs.value.push(parsed);
      }
    } catch (error) {
      console.error('Error reading file:', file.name, error);
    }
  }

  isProcessing.value = false;
  processingProgress.value = { current: 0, total: 0, fileName: '' };
};

// Helper to filter data by time range
const getTimeRangeMs = () => {
  switch (timeRange.value) {
    case '1d': return 1 * 24 * 60 * 60 * 1000;
    case '3d': return 3 * 24 * 60 * 60 * 1000;
    case '7d': return 7 * 24 * 60 * 60 * 1000;
    case '30d': return 30 * 24 * 60 * 60 * 1000;
    default: return null; // 'all'
  }
};

// Find latest timestamp across ALL visible logs
const getLatestTimestampFromAllLogs = () => {
  const visibleLogs = logs.value.filter(l => l.visible !== false);
  let latestTime = 0;

  visibleLogs.forEach(log => {
    if (log.data && log.data.length > 0) {
      log.data.forEach(d => {
        const time = new Date(d.Date).getTime();
        if (time > latestTime) latestTime = time;
      });
    }
  });

  return latestTime;
};

// Calculate automatic sampling rate based on data count and time range
// Pravidla:
// - Pod 3 hodiny = 1s vzorkovani (plne rozliseni)
// - Nad 3 hodiny = dynamicky tak, aby v okne bylo 5-10k hodnot
const calculateAutoSamplingRate = (dataCount, timeRangeMinutes) => {
  // Pod 3 hodiny = vzdy plne rozliseni
  if (timeRangeMinutes <= 180) {
    return 1;
  }

  // Target: 5000-10000 points for good balance between detail and performance
  const TARGET_POINTS = 7500;

  // If data count is already in target range, no sampling needed
  if (dataCount <= TARGET_POINTS) {
    return 1;
  }

  // Calculate sampling rate to achieve target points
  const samplingRate = Math.ceil(dataCount / TARGET_POINTS);
  return samplingRate;
};

const filterDataByTimeRange = (data, latestTimestamp, zoomRange = null) => {
  if (!data || data.length === 0) return data;

  let filteredData = data;

  // Pokud mame zoom range, filtrujeme podle nej
  if (zoomRange) {
    filteredData = filteredData.filter(d => {
      // Pouzij predpocitany _timestamp misto parsovani Date stringu
      const t = d._timestamp || new Date(d.Date).getTime();
      return t >= zoomRange.start && t <= zoomRange.end;
    });
  } else {
    // Apply time range filter from UI selector
    const rangeMs = getTimeRangeMs();
    if (rangeMs) {
      const cutoffTime = latestTimestamp - rangeMs;
      filteredData = filteredData.filter(d => {
        const t = d._timestamp || new Date(d.Date).getTime();
        return t >= cutoffTime;
      });
    }
  }

  // Calculate time range in minutes for auto sampling
  let timeRangeMinutes;
  if (filteredData.length > 1) {
    const firstTime = new Date(filteredData[0].Date).getTime();
    const lastTime = new Date(filteredData[filteredData.length - 1].Date).getTime();
    timeRangeMinutes = (lastTime - firstTime) / 1000 / 60;
  } else {
    timeRangeMinutes = 0;
  }

  // Apply automatic sampling rate based on time range
  const autoSamplingRate = calculateAutoSamplingRate(filteredData.length, timeRangeMinutes);

  if (autoSamplingRate > 1 && filteredData.length > 0) {
    const sampledData = [];
    for (let i = 0; i < filteredData.length; i += autoSamplingRate) {
      sampledData.push(filteredData[i]);
    }
    filteredData = sampledData;
  }

  return filteredData;
};

// Plotly Chart rendering
const renderChart = () => {

  if (!chartDiv.value || selectedParams.value.length === 0 || logs.value.length === 0) {
    return;
  }

  const visibleLogs = logs.value.filter(l => l.visible !== false);
  if (visibleLogs.length === 0) return;

  // Get latest timestamp from all logs first
  const latestTimestamp = getLatestTimestampFromAllLogs();

  const traces = [];

  // Get date range for title
  let dateTitle = '';

  visibleLogs.forEach((log, logIndex) => {

    // Filter data by time range
    // Priorita: 1) currentZoomRange (manualni zoom), 2) selectedDefrost, 3) timeRange selector
    let zoomRange = currentZoomRange.value;
    if (!zoomRange && selectedDefrost.value) {
      // Defrost zoom: ±10 min kolem defrostu (pouze pokud neni manualni zoom)
      const startMs = new Date(selectedDefrost.value.startTime).getTime();
      const endMs = new Date(selectedDefrost.value.endTime).getTime();
      zoomRange = {
        start: startMs - 10 * 60 * 1000,
        end: endMs + 10 * 60 * 1000
      };
    }
    const filteredData = filterDataByTimeRange(log.data, latestTimestamp, zoomRange);

    if (!filteredData || filteredData.length === 0) {
      return;
    }

    // Convert dates to local ISO format for Plotly (without UTC conversion)
    const x = filteredData.map(d => toLocalISOString(d.Date));

    // Extract date for title (from first log's first entry)
    if (logIndex === 0 && x.length > 0) {
      const firstDate = new Date(x[0]);
      const lastDate = new Date(x[x.length - 1]);
      const formatDate = (d) => d.toLocaleDateString('cs-CZ', { day: '2-digit', month: '2-digit', year: 'numeric' });
      if (formatDate(firstDate) === formatDate(lastDate)) {
        dateTitle = formatDate(firstDate);
      } else {
        dateTitle = `${formatDate(firstDate)} - ${formatDate(lastDate)}`;
      }
    }

    // Log first data row keys for debugging
    if (logIndex === 0) {
    }

    selectedParams.value.forEach((param, paramIndex) => {
      // Hodnota ~327.67 nebo ~-327.67 znamená nezapojené čidlo - nahradíme null pro nevykreslení
      // Také filtrujeme hodnoty > 300 pro teplotní čidla (nerealistické hodnoty)
      const isTemperatureParam = param.includes('temp') || param.includes('Temp') ||
                                  param.includes('Evap') || param.includes('Cond') ||
                                  param.startsWith('T1') || param.startsWith('T2') ||
                                  param.startsWith('T3') || param.startsWith('T4') ||
                                  param.startsWith('T5') || param.startsWith('T6') ||
                                  param.startsWith('T7');
      const y = filteredData.map(d => {
        const val = d[param];
        if (val === undefined || val === null) return null;
        // Nezapojené čidlo: hodnota kolem ±327 nebo pro teploty > 300°C (nerealistické)
        const absVal = Math.abs(val);
        if (absVal > 326 && absVal < 330) return null;
        if (isTemperatureParam && absVal > 200) return null; // Teploty nad 200°C jsou nerealistické
        return val;
      });
      const validY = y.filter(v => v !== undefined && v !== null);

      // Pokud nejsou žádné validní hodnoty (např. nezapojené čidlo), přeskočíme
      if (validY.length === 0) {
        return;
      }

      const paramGroup = Object.values(PARAMETER_GROUPS).find(g =>
        g.params.some(p => p.key === param)
      );
      const paramDef = paramGroup?.params.find(p => p.key === param);
      const traceColor = LOG_COLORS[(logIndex * selectedParams.value.length + paramIndex) % LOG_COLORS.length];

      // Speed parametry: hodnota v datech je /100, zobrazime jako plne RPM (napr. 3060 RPM)
      const isSpeedParam = param.toLowerCase().includes('speed');
      const hoverTemplate = isSpeedParam
        ? `%{customdata:.0f} RPM<extra></extra>`
        : `%{y:.2f} ${paramDef?.label || param}<extra></extra>`;

      // Pro Speed parametry vytvorime customdata s plnym RPM (hodnota * 100)
      const customData = isSpeedParam ? y.map(v => v !== null ? v * 100 : null) : undefined;

      traces.push({
        x,
        y,
        ...(customData && { customdata: customData }),
        type: 'scatter',
        mode: 'lines',
        name: `${paramDef?.label || param}${visibleLogs.length > 1 ? ` (${log.fileName})` : ''}`,
        line: {
          color: traceColor,
          width: 1.5
        },
        hovertemplate: hoverTemplate,
        hoverlabel: {
          bgcolor: traceColor,
          bordercolor: traceColor,
          font: { color: getContrastColor(traceColor), size: 12 }
        }
      });
    });
  });

  // Theme-based colors
  const isLight = lightTheme.value;
  const colors = isLight ? {
    paper: '#ffffff',
    plot: '#f8fafc',
    font: '#1e293b',
    grid: 'rgba(100,116,139,0.2)',
    legendBg: 'rgba(255,255,255,0.9)'
  } : {
    paper: 'rgba(0,0,0,0)',
    plot: 'rgba(30,41,59,0.5)',
    font: '#94a3b8',
    grid: 'rgba(71,85,105,0.3)',
    legendBg: 'rgba(0,0,0,0)'
  };

  const layout = {
    paper_bgcolor: colors.paper,
    plot_bgcolor: colors.plot,
    font: { color: colors.font, size: 11 },
    margin: { l: 50, r: 20, t: 40, b: 60 },
    xaxis: {
      type: 'date',
      gridcolor: colors.grid,
      tickformat: '%H:%M:%S',
      tickangle: 0,
      tickcolor: colors.font,
      linecolor: colors.grid,
      title: { text: dateTitle, font: { color: colors.font, size: 11 }, standoff: 10 },
      showspikes: true,
      spikemode: 'across',
      spikethickness: 1,
      spikecolor: colors.grid,
      spikedash: 'dot',
      // Zoom priorita: 1) currentZoomRange (manualni), 2) selectedDefrost, 3) autorange
      ...(currentZoomRange.value ? {
        range: [
          toLocalISOString(currentZoomRange.value.start),
          toLocalISOString(currentZoomRange.value.end)
        ],
        autorange: false
      } : selectedDefrost.value ? {
        range: [
          toLocalISOString(new Date(selectedDefrost.value.startTime).getTime() - 10 * 60 * 1000),
          toLocalISOString(new Date(selectedDefrost.value.endTime).getTime() + 10 * 60 * 1000)
        ],
        autorange: false
      } : {
        autorange: true
      })
    },
    yaxis: {
      gridcolor: colors.grid,
      tickcolor: colors.font,
      linecolor: colors.grid,
      title: { text: '', font: { color: colors.font } },
      // Aplikuj Y zoom rozsah pokud je definovan
      ...(currentZoomRange.value && currentZoomRange.value.yMin !== null && currentZoomRange.value.yMax !== null ? {
        range: [currentZoomRange.value.yMin, currentZoomRange.value.yMax],
        autorange: false
      } : {
        autorange: true
      })
    },
    legend: {
      orientation: 'h',
      y: 1.02,
      x: 0.5,
      xanchor: 'center',
      yanchor: 'bottom',
      bgcolor: colors.legendBg,
      font: { size: 10, color: colors.font }
    },
    hovermode: 'x',
    hoverlabel: {
      bgcolor: isLight ? 'rgba(255,255,255,0.95)' : 'rgba(30,41,59,0.95)',
      bordercolor: isLight ? 'rgba(0,0,0,0.2)' : 'rgba(255,255,255,0.2)',
      font: { color: colors.font, size: 11 },
      namelength: -1
    },
    hoverdistance: 100,
    spikedistance: 100,
    dragmode: 'zoom',
    shapes: [],
    annotations: []
  };

  // Pridej defrost zony pokud jsou zapnuty
  if (showDefrostZones.value && defrosts.value.length > 0) {
    defrosts.value.forEach((defrost, idx) => {
      const startIso = toLocalISOString(defrost.startTime);
      const endIso = toLocalISOString(defrost.endTime);
      const isSelected = selectedDefrost.value?.id === defrost.id;

      // Obdelnik pro defrost zonu
      layout.shapes.push({
        type: 'rect',
        xref: 'x',
        yref: 'paper',
        x0: startIso,
        x1: endIso,
        y0: 0,
        y1: 1,
        fillcolor: isSelected ? 'rgba(56, 189, 248, 0.25)' : 'rgba(6, 182, 212, 0.15)',
        line: {
          color: isSelected ? 'rgba(56, 189, 248, 0.8)' : 'rgba(6, 182, 212, 0.5)',
          width: isSelected ? 2 : 1
        },
        layer: 'below'
      });

      // Anotace s cislem defrostu
      layout.annotations.push({
        x: startIso,
        y: 1,
        xref: 'x',
        yref: 'paper',
        text: `D${defrost.id}`,
        showarrow: false,
        font: {
          size: 9,
          color: isLight ? '#0e7490' : '#67e8f9'
        },
        bgcolor: isLight ? 'rgba(255,255,255,0.8)' : 'rgba(30,41,59,0.8)',
        borderpad: 2,
        yanchor: 'bottom'
      });
    });
  }

  const config = {
    displayModeBar: true,
    responsive: true,
    modeBarButtonsToRemove: ['lasso2d', 'select2d'],
    displaylogo: false
  };

  if (traces.length > 0) {
    // Find min/max of y values
    const yValues = traces[0].y.filter(v => v !== null && v !== undefined && !isNaN(v));
  }

  Plotly.newPlot(chartDiv.value, traces, layout, config);

  // Handler pro zoom/pan - pri zoomu pod 3h prenacti data s jemnejsim vzorkovanim
  chartDiv.value.on('plotly_relayout', (eventData) => {
    // Ignoruj eventy ktere jsme sami zpusobili
    if (isRenderingFromZoom.value) {
      return;
    }

    // Detekce zoomu v ose X
    if (eventData['xaxis.range[0]'] && eventData['xaxis.range[1]']) {
      // Použij parseLocalDateTime pro správné parsování lokálního času z Plotly
      const newStart = parseLocalDateTime(eventData['xaxis.range[0]']);
      const newEnd = parseLocalDateTime(eventData['xaxis.range[1]']);
      const rangeMinutes = (newEnd - newStart) / 1000 / 60;

      // Zachytíme i Y rozsah pokud je definován
      const yMin = eventData['yaxis.range[0]'];
      const yMax = eventData['yaxis.range[1]'];


      // Ulozime novy zoom range (tento ma prednost pred defrost zoomem)
      const oldZoom = currentZoomRange.value;
      currentZoomRange.value = {
        start: newStart,
        end: newEnd,
        yMin: yMin !== undefined ? yMin : null,
        yMax: yMax !== undefined ? yMax : null
      };

      // Re-renderuj pro jemnejsi vzorkovani - ale jen pokud se zoom zmenil vyznamne
      // nebo pokud jsme pod 3h kde chceme plne rozliseni
      const needsRerender = rangeMinutes <= 180 || !oldZoom;

      if (needsRerender) {
        isRenderingFromZoom.value = true;
        setTimeout(() => {
          renderChart();
          // Reset flag az po renderovani
          setTimeout(() => {
            isRenderingFromZoom.value = false;
          }, 200);
        }, 50);
      }
    }

    // Detekce zoomu pouze v ose Y (bez změny X)
    if (eventData['yaxis.range[0]'] !== undefined && eventData['yaxis.range[1]'] !== undefined &&
        !eventData['xaxis.range[0]']) {
      const yMin = eventData['yaxis.range[0]'];
      const yMax = eventData['yaxis.range[1]'];

      if (currentZoomRange.value) {
        currentZoomRange.value = {
          ...currentZoomRange.value,
          yMin,
          yMax
        };
      }
      // Y-only zoom nepotrebuje rerender dat
    }

    // Reset zoomu (double-click)
    if (eventData['xaxis.autorange'] === true) {
      currentZoomRange.value = null;
      isRenderingFromZoom.value = true;
      setTimeout(() => {
        renderChart();
        setTimeout(() => {
          isRenderingFromZoom.value = false;
        }, 200);
      }, 50);
    }
  });

  // Pridej click handler pro defrost zony
  if (showDefrostZones.value && defrosts.value.length > 0) {
    chartDiv.value.on('plotly_click', (data) => {
      if (data.points && data.points.length > 0) {
        // Plotly vraci x jako string v lokalnim formatu - pouzij parseLocalDateTime
        const clickedTime = parseLocalDateTime(data.points[0].x);
        // Najdi defrost ve kterem je klik
        const clickedDefrost = defrosts.value.find(d => {
          const start = new Date(d.startTime).getTime();
          const end = new Date(d.endTime).getTime();
          return clickedTime >= start && clickedTime <= end;
        });
        if (clickedDefrost) {
          selectedDefrost.value = clickedDefrost;
          currentZoomRange.value = null; // Reset manualni zoom - defrost zoom ma prednost
          renderChart(); // Re-render pro zvyrazneni vyberu a zoom na defrost
        }
      }
    });
  }

};

// Mark chart as needing update when params change (don't auto-render)
watch(selectedParams, () => {
  chartNeedsUpdate.value = true;
}, { deep: true });

// Auto-render only when logs change or tab switches to chart
watch([logs, activeTab], () => {
  if (activeTab.value === 'chart' && logs.value.length > 0) {
    nextTick(() => {
      renderChart();
      chartNeedsUpdate.value = false;
    });
  }
  // Trigger defrost analysis when switching to defrosts tab
  if (activeTab.value === 'defrosts' && logs.value.length > 0 && !defrostAnalysisResult.value && !defrostAnalysisProgress.value.processing) {
    runDefrostAnalysis();
  }
}, { deep: true });

// Re-run defrost analysis when active log changes (while on defrosts tab)
watch(activeLogIndex, () => {
  if (activeTab.value === 'defrosts' && logs.value.length > 0) {
    defrostAnalysisResult.value = null;
    runDefrostAnalysis();
  }
});

// Render chart when modal opens
watch(() => props.isOpen, (isOpen) => {
  if (isOpen && activeTab.value === 'chart' && logs.value.length > 0) {
    nextTick(() => {
      setTimeout(renderChart, 100);
    });
  }
});

// Cleanup on unmount
onUnmounted(() => {
  if (chartDiv.value) {
    Plotly.purge(chartDiv.value);
  }
});
</script>

<style scoped>
.modal-enter-active,
.modal-leave-active {
  transition: opacity 0.3s ease;
}

.modal-enter-from,
.modal-leave-to {
  opacity: 0;
}

.modal-enter-active > div,
.modal-leave-active > div {
  transition: transform 0.3s ease;
}

.modal-enter-from > div,
.modal-leave-to > div {
  transform: scale(0.9);
}

/* Custom scrollbar */
::-webkit-scrollbar {
  width: 6px;
}

::-webkit-scrollbar-track {
  background: transparent;
}

::-webkit-scrollbar-thumb {
  background: #475569;
  border-radius: 3px;
}

::-webkit-scrollbar-thumb:hover {
  background: #64748b;
}
</style>
